import { supabase } from "./supabase";
import type {
  ClassificationDefinition,
  ClassificationOption,
  LeadClassificationValue,
} from "../features/classifications/contracts";
import type { NoticeState } from "../features/notifications/feed";
import type { UserPreference } from "../features/preferences/contracts";
import { isAllowedMeetingLink, type Meeting } from "../features/meetings/contracts";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { ActivityEvent, AppData, Company, Contact, Funnel, LeadAttachment, LeadContact, LeadFieldDefinition, LeadFieldHeight, LeadFieldOption, LeadFieldType, LeadFieldValue, LeadFieldWidth, LeadFormSection, LeadLink, LeadRelation, LossReason, Membership, SavedView, Stage, Tag, Task, Trigger } from "./types";
import {
  commandActorTypes,
  commandError,
  commandOrigins,
  createCommandContext,
  taskActionTypes,
  taskOrigins,
  type CommandError,
  type CommandContext,
  type CommandResult,
} from "./command-contract";
import {
  taskDraftSchema,
  type TaskCommandValue,
  type TaskDraftInput,
} from "../features/tasks/commands";

type RemoteActivityEventInput = Pick<
  ActivityEvent,
  "id" | "leadId" | "type" | "title" | "description" | "occurredAt"
> & Partial<Pick<ActivityEvent, "membershipId" | "taskId" | "stageId" | "countsAsInteraction">>;
import type {
  CreateOrReuseCompanyLeadInput,
  CreateRelatedLeadInput,
  MergeCompaniesInput,
  ReclassifyPartyChannelInput,
  ReviewIdentityCandidateInput,
} from "../features/identity/commands";
import { countsAsInteraction } from "./activity";
import {
  mapCompanyRow,
  mapContactRow,
  mapIdentityCandidateRow,
  mapLeadContactRow,
  mapLeadRelationRow,
  mapPartyChannelRow,
} from "../features/identity/mappers";
import {
  normalizeFlowExecutionMode,
  normalizeFunnelStructureType,
} from "../features/flows/schema";
import {
  identityRemote,
  loadIdentityCapabilities,
  loadIdentityRows,
} from "../features/identity/repository";

const empty: AppData = {
  identityCapabilityStatus: "not_installed",
  identityCapabilitySchemaVersion: 0,
  identityFoundationAvailable: false,
  canonicalLeadCreationAvailable: false,
  canonicalLeadCreationCanary: false,
  emailInboxAvailable: false,
  emailSendAvailable: false,
  aiAssistanceAvailable: false,
  aiAutoSendAvailable: false,
  flowDefinitionsAvailable: false,
  flowPublicationAvailable: false,
  flowExecutionAvailable: false,
  financeAvailable: false,
  companies: [],
  contacts: [],
  partyChannels: [],
  leadContacts: [],
  leadRelations: [],
  identityMatchCandidates: [],
  commandReceipts: [],
  auditLog: [],
  externalConnections: [],
  outboxJobs: [],
  automationJobs: [],
  jobAttempts: [],
  automationIncidents: [],
  featureFlags: [],
  googleTaskBindings: [],
  taskExternalLinks: [],
  calendarReminderBindings: [],
  googleSyncCheckpoints: [],
  leads: [],
  funnels: [],
  stages: [],
  memberships: [],
  tasks: [],
  events: [],
  tags: [],
  triggers: [],
  lossReasons: [],
  savedViews: [],
  attachments: [],
  links: [],
  formSections: [],
  leadFields: [],
  leadFieldOptions: [],
  leadFieldValues: [],
};
const client = () => {
  if (!supabase) throw new Error("Supabase não configurado");
  return supabase;
};

type RpcRecord = Record<string, unknown>;

const taskTransitions = ["created", "completed", "reopened"] as const;
const taskStatuses = ["pending", "done"] as const;
// Coluna nova: linha remota antiga não a traz, e "a fazer" é o único default
// honesto. Valor desconhecido continua sendo linha inválida (fail-closed).
const taskProgressValues = ["todo", "doing", "waiting"] as const;
const taskPriorities = ["low", "medium", "high"] as const;
const membershipStatuses = ["active", "won", "lost"] as const;
const savedViewPeriods = ["all", "today", "yesterday", "24h", "7d", "30d", "90d", "custom"] as const;
const leadFieldTypes = ["text", "textarea", "number", "currency", "url", "date", "boolean", "select", "multi_select"] as const satisfies readonly LeadFieldType[];
const leadFieldWidths = ["half", "full"] as const satisfies readonly LeadFieldWidth[];
const leadFieldHeights = ["compact", "normal", "expanded"] as const satisfies readonly LeadFieldHeight[];
const leadFilesBucket = "lead-files";
const maxLeadFileBytes = 25 * 1024 * 1024;
const attachmentMimeTypes = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "text/plain",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
] as const;
const photoMimeTypes = ["image/jpeg", "image/png", "image/webp"] as const;
const photoExtensions: Record<(typeof photoMimeTypes)[number], string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};
const commandResultErrorCodes = [
  "validation_error",
  "unauthenticated",
  "forbidden",
  "not_found",
  "invalid_state",
  "version_conflict",
  "idempotency_conflict",
  "persistence_error",
  "provider_timeout",
  "provider_transient",
  "provider_permanent",
  "provider_rate_limited",
  "provider_revoked",
  "ambiguous_external_result",
  "lease_expired",
  "dead_letter",
] as const;
const commandResultSeverities = ["info", "warning", "error", "critical"] as const;

function isRecord(value: unknown): value is RpcRecord {
  try {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  } catch {
    return false;
  }
}

function rpcRecord(value: unknown, invalidMessage: string): RpcRecord {
  if (!isRecord(value)) throw new Error(invalidMessage);
  return value;
}

function rpcField(payload: RpcRecord, key: string, invalidMessage: string): unknown {
  try {
    return Object.prototype.hasOwnProperty.call(payload, key) ? payload[key] : undefined;
  } catch {
    throw new Error(invalidMessage);
  }
}

function requiredRpcString(payload: RpcRecord, key: string, invalidMessage: string): string {
  const value = rpcField(payload, key, invalidMessage);
  const normalized = typeof value === "string" ? value.trim() : "";
  if (!normalized) throw new Error(`${invalidMessage}: ${key}.`);
  return normalized;
}

function optionalRpcString(payload: RpcRecord, key: string, invalidMessage: string): string | undefined {
  const value = rpcField(payload, key, invalidMessage);
  if (value === null || value === undefined) return undefined;
  const normalized = typeof value === "string" ? value.trim() : "";
  if (!normalized) throw new Error(`${invalidMessage}: ${key}.`);
  return normalized;
}

function optionalRpcText(payload: RpcRecord, key: string, invalidMessage: string): string | undefined {
  const value = rpcField(payload, key, invalidMessage);
  if (value === null || value === undefined) return undefined;
  if (typeof value !== "string") throw new Error(`${invalidMessage}: ${key}.`);
  const normalized = value.trim();
  return normalized || undefined;
}

function requiredRpcBoolean(payload: RpcRecord, key: string, invalidMessage: string): boolean {
  const value = rpcField(payload, key, invalidMessage);
  if (typeof value !== "boolean") throw new Error(`${invalidMessage}: ${key}.`);
  return value;
}

function optionalRpcBoolean(payload: RpcRecord, key: string, invalidMessage: string): boolean | undefined {
  const value = rpcField(payload, key, invalidMessage);
  if (value === null || value === undefined) return undefined;
  if (typeof value !== "boolean") throw new Error(`${invalidMessage}: ${key}.`);
  return value;
}

function requiredRpcSafeInteger(payload: RpcRecord, key: string, minimum: number, invalidMessage: string): number {
  const value = rpcField(payload, key, invalidMessage);
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < minimum)
    throw new Error(`${invalidMessage}: ${key}.`);
  return value;
}

function optionalRpcSafeInteger(payload: RpcRecord, key: string, minimum: number, invalidMessage: string): number | undefined {
  const value = rpcField(payload, key, invalidMessage);
  if (value === null || value === undefined) return undefined;
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < minimum)
    throw new Error(`${invalidMessage}: ${key}.`);
  return value;
}

function requiredRpcEnum<T extends string>(
  payload: RpcRecord,
  key: string,
  allowed: readonly T[],
  invalidMessage: string,
): T {
  const value = requiredRpcString(payload, key, invalidMessage);
  if (!allowed.includes(value as T)) throw new Error(`${invalidMessage}: ${key}.`);
  return value as T;
}

/** Enum opcional: ausente vira `undefined`, valor fora da lista é linha inválida. */
function optionalRpcEnum<T extends string>(
  payload: RpcRecord,
  key: string,
  allowed: readonly T[],
  invalidMessage: string,
): T | undefined {
  const value = optionalRpcString(payload, key, invalidMessage);
  if (value === undefined) return undefined;
  if (!allowed.includes(value as T)) throw new Error(`${invalidMessage}: ${key}.`);
  return value as T;
}

function isDenseRpcArray(value: unknown): value is unknown[] {
  try {
    if (!Array.isArray(value)) return false;
    for (let index = 0; index < value.length; index += 1) {
      if (!Object.prototype.hasOwnProperty.call(value, index)) return false;
    }
    return true;
  } catch {
    return false;
  }
}

function remoteRows(value: unknown, table: string): unknown[] {
  if (value === null || value === undefined) return [];
  if (!isDenseRpcArray(value)) throw new Error(`A resposta remota de ${table} é inválida.`);
  return value;
}

function requiredRpcStringArray(payload: RpcRecord, key: string, invalidMessage: string): string[] {
  const value = rpcField(payload, key, invalidMessage);
  try {
    if (!isDenseRpcArray(value)) throw new Error();
    const normalized: string[] = [];
    for (let index = 0; index < value.length; index += 1) {
      const item = value[index];
      if (typeof item !== "string" || !item.trim()) throw new Error();
      normalized.push(item.trim());
    }
    return normalized;
  } catch {
    throw new Error(`${invalidMessage}: ${key}.`);
  }
}

function optionalRpcDetails(payload: RpcRecord, key: string, invalidMessage: string): Record<string, unknown> | undefined {
  const value = rpcField(payload, key, invalidMessage);
  if (value === null || value === undefined) return undefined;
  const details = rpcRecord(value, `${invalidMessage}: ${key}.`);
  try {
    return Object.fromEntries(Object.keys(details).map((detailKey) => [detailKey, details[detailKey]]));
  } catch {
    throw new Error(`${invalidMessage}: ${key}.`);
  }
}

function requiredRpcNonNegativeNumber(payload: RpcRecord, key: string, invalidMessage: string): number {
  const value = rpcField(payload, key, invalidMessage);
  const parsed = typeof value === "number"
    ? value
    : typeof value === "string" && value.trim()
      ? Number(value)
      : Number.NaN;
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > Number.MAX_SAFE_INTEGER)
    throw new Error(`${invalidMessage}: ${key}.`);
  return parsed;
}

function hasStorageControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

function requiredStorageSegment(value: unknown, key: string, invalidMessage: string): string {
  if (
    typeof value !== "string" ||
    !value ||
    value.length > 255 ||
    value !== value.trim() ||
    value === "." ||
    value === ".." ||
    value.includes("/") ||
    value.includes("\\") ||
    hasStorageControlCharacter(value)
  ) {
    throw new Error(`${invalidMessage}: ${key}.`);
  }
  return value;
}

function requiredStoragePath(value: unknown, key: string, invalidMessage: string): string {
  if (typeof value !== "string" || !value || value.length > 1024 || value !== value.trim())
    throw new Error(`${invalidMessage}: ${key}.`);
  const segments = value.split("/");
  if (segments.length < 3) throw new Error(`${invalidMessage}: ${key}.`);
  for (const segment of segments) requiredStorageSegment(segment, key, invalidMessage);
  return value;
}

function optionalRemoteStoragePath(payload: RpcRecord, key: string, invalidMessage: string): string | undefined {
  const value = rpcField(payload, key, invalidMessage);
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string" && !value.trim()) return undefined;
  return requiredStoragePath(value, key, invalidMessage);
}

function assertOwnedStoragePath(
  value: string,
  ownerId: string,
  leadId: string,
  invalidMessage: string,
): string {
  const path = requiredStoragePath(value, "storage_path", invalidMessage);
  const ownerSegment = requiredStorageSegment(ownerId, "owner_id", invalidMessage);
  const leadSegment = requiredStorageSegment(leadId, "lead_id", invalidMessage);
  const prefix = `${ownerSegment}/${leadSegment}/`;
  if (!path.startsWith(prefix)) throw new Error(`${invalidMessage}: storage_path.`);
  return path;
}

async function requireOwnedRemoteRow(
  c: SupabaseClient,
  table: string,
  ownerId: unknown,
  id: unknown,
  idKey: string,
  select: string,
  invalidMessage: string,
): Promise<{ ownerId: string; id: string; row: RpcRecord }> {
  const safeOwnerId = requiredInputIdentifier(ownerId, "owner_id", invalidMessage);
  const safeId = requiredInputIdentifier(id, idKey, invalidMessage);
  const result = await c
    .from(table)
    .select(select)
    .eq("id", safeId)
    .eq("owner_id", safeOwnerId)
    .maybeSingle();
  if (result.error) throw result.error;
  if (!result.data) throw new Error(`${invalidMessage}: ${idKey}.`);
  const row = rpcRecord(result.data, invalidMessage);
  const rowOwnerId = requiredInputIdentifier(
    requiredRpcString(row, "owner_id", invalidMessage),
    "owner_id",
    invalidMessage,
  );
  if (rowOwnerId !== safeOwnerId) throw new Error(`${invalidMessage}: owner_id.`);
  return { ownerId: safeOwnerId, id: safeId, row };
}

async function requireOwnedLead(
  c: SupabaseClient,
  ownerId: unknown,
  leadId: unknown,
  invalidMessage: string,
): Promise<{ ownerId: string; leadId: string }> {
  const reference = await requireOwnedRemoteRow(c, "leads", ownerId, leadId, "lead_id", "owner_id", invalidMessage);
  return { ownerId: reference.ownerId, leadId: reference.id };
}

async function requireOwnedTaskReferences(
  c: SupabaseClient,
  ownerId: unknown,
  leadId: unknown,
  membershipId: unknown,
  invalidMessage: string,
): Promise<{ ownerId: string; leadId: string; membershipId?: string }> {
  const lead = await requireOwnedLead(c, ownerId, leadId, invalidMessage);
  if (membershipId === undefined) return lead;
  const membership = await requireOwnedRemoteRow(
    c,
    "pipeline_memberships",
    lead.ownerId,
    membershipId,
    "membership_id",
    "owner_id,lead_id",
    invalidMessage,
  );
  const membershipLeadId = requiredInputIdentifier(
    requiredRpcString(membership.row, "lead_id", invalidMessage),
    "lead_id",
    invalidMessage,
  );
  if (membershipLeadId !== lead.leadId)
    throw new Error(`${invalidMessage}: membership_id/lead_id.`);
  return { ...lead, membershipId: membership.id };
}

async function requireOwnedLeadContext(
  c: SupabaseClient,
  ownerId: unknown,
  leadId: unknown,
  invalidMessage: string,
): Promise<{ ownerId: string; leadId: string; funnelId?: string }> {
  const reference = await requireOwnedRemoteRow(c, "leads", ownerId, leadId, "lead_id", "owner_id,funnel_id", invalidMessage);
  return {
    ownerId: reference.ownerId,
    leadId: reference.id,
    funnelId: optionalRpcString(reference.row, "funnel_id", invalidMessage),
  };
}

async function requireOwnedCatalogReferences(
  c: SupabaseClient,
  table: "tags" | "triggers",
  ownerId: unknown,
  ids: unknown[],
  idKey: "tag_id" | "trigger_id",
  expectedFunnelId: string | undefined,
  invalidMessage: string,
): Promise<string[]> {
  const safeOwnerId = requiredInputIdentifier(ownerId, "owner_id", invalidMessage);
  const safeFunnelId = expectedFunnelId === undefined
    ? undefined
    : requiredInputIdentifier(expectedFunnelId, "funnel_id", invalidMessage);
  const safeIds = ids.map((id) => requiredInputIdentifier(id, idKey, invalidMessage));
  for (const safeId of safeIds) {
    const reference = await requireOwnedRemoteRow(
      c,
      table,
      safeOwnerId,
      safeId,
      idKey,
      "owner_id,funnel_id",
      invalidMessage,
    );
    const catalogFunnelId = optionalRpcString(reference.row, "funnel_id", invalidMessage);
    if (safeFunnelId !== undefined && catalogFunnelId !== safeFunnelId)
      throw new Error(`${invalidMessage}: ${idKey}/funnel_id.`);
  }
  return safeIds;
}

async function requireOwnedSavedViewReferences(
  c: SupabaseClient,
  ownerId: unknown,
  view: SavedView,
  invalidMessage: string,
): Promise<void> {
  const safeOwnerId = requiredInputIdentifier(ownerId, "owner_id", invalidMessage);
  const safeFunnelId = view.funnelId === undefined
    ? undefined
    : requiredInputIdentifier(view.funnelId, "funnel_id", invalidMessage);
  if (safeFunnelId !== undefined)
    await requireOwnedRemoteRow(c, "funnels", safeOwnerId, safeFunnelId, "funnel_id", "owner_id", invalidMessage);

  const references = [
    { id: view.stageId, table: "stages", idKey: "stage_id" },
    { id: view.reasonId, table: "loss_reasons", idKey: "reason_id" },
    { id: view.tagId, table: "tags", idKey: "tag_id" },
    { id: view.triggerId, table: "triggers", idKey: "trigger_id" },
  ] as const;
  const inferredFunnelIds = new Set<string>();
  for (const referenceInput of references) {
    if (referenceInput.id === undefined) continue;
    const reference = await requireOwnedRemoteRow(
      c,
      referenceInput.table,
      safeOwnerId,
      referenceInput.id,
      referenceInput.idKey,
      "owner_id,funnel_id",
      invalidMessage,
    );
    const referenceFunnelId = optionalRpcString(reference.row, "funnel_id", invalidMessage);
    if (safeFunnelId !== undefined && referenceFunnelId !== safeFunnelId)
      throw new Error(`${invalidMessage}: ${referenceInput.idKey}/funnel_id.`);
    if (referenceFunnelId !== undefined) inferredFunnelIds.add(referenceFunnelId);
  }
  if (safeFunnelId === undefined && inferredFunnelIds.size > 1)
    throw new Error(`${invalidMessage}: funnel_id.`);
}

async function requireOwnedFormSection(
  c: SupabaseClient,
  ownerId: unknown,
  sectionId: unknown,
  funnelId: unknown,
  invalidMessage: string,
): Promise<{ ownerId: string; sectionId: string; funnelId: string }> {
  const safeFunnelId = requiredInputIdentifier(funnelId, "funnel_id", invalidMessage);
  const reference = await requireOwnedRemoteRow(
    c,
    "lead_form_sections",
    ownerId,
    sectionId,
    "section_id",
    "owner_id,funnel_id",
    invalidMessage,
  );
  const sectionFunnelId = requiredInputIdentifier(
    requiredRpcString(reference.row, "funnel_id", invalidMessage),
    "funnel_id",
    invalidMessage,
  );
  if (sectionFunnelId !== safeFunnelId) throw new Error(`${invalidMessage}: funnel_id/section_id.`);
  return { ownerId: reference.ownerId, sectionId: reference.id, funnelId: safeFunnelId };
}

async function requireOwnedActivityReferences(
  c: SupabaseClient,
  ownerId: unknown,
  leadId: unknown,
  membershipId: string | undefined,
  taskId: string | undefined,
  stageId: string | undefined,
  invalidMessage: string,
): Promise<{ ownerId: string; leadId: string; membershipId?: string; taskId?: string; stageId?: string }> {
  const lead = await requireOwnedLeadContext(c, ownerId, leadId, invalidMessage);
  let safeMembershipId: string | undefined;
  let membershipFunnelId: string | undefined;
  if (membershipId !== undefined) {
    const membership = await requireOwnedRemoteRow(
      c,
      "pipeline_memberships",
      lead.ownerId,
      membershipId,
      "membership_id",
      "owner_id,lead_id,funnel_id",
      invalidMessage,
    );
    const membershipLeadId = requiredInputIdentifier(
      requiredRpcString(membership.row, "lead_id", invalidMessage),
      "lead_id",
      invalidMessage,
    );
    if (membershipLeadId !== lead.leadId) throw new Error(`${invalidMessage}: membership_id/lead_id.`);
    safeMembershipId = membership.id;
    membershipFunnelId = requiredInputIdentifier(
      requiredRpcString(membership.row, "funnel_id", invalidMessage),
      "funnel_id",
      invalidMessage,
    );
    if (lead.funnelId !== undefined && membershipFunnelId !== lead.funnelId)
      throw new Error(`${invalidMessage}: membership_id/funnel_id.`);
  }
  let safeStageId: string | undefined;
  if (stageId !== undefined) {
    const stage = await requireOwnedRemoteRow(
      c,
      "stages",
      lead.ownerId,
      stageId,
      "stage_id",
      "owner_id,funnel_id",
      invalidMessage,
    );
    const stageFunnelId = requiredInputIdentifier(
      requiredRpcString(stage.row, "funnel_id", invalidMessage),
      "funnel_id",
      invalidMessage,
    );
    if (lead.funnelId !== undefined && stageFunnelId !== lead.funnelId)
      throw new Error(`${invalidMessage}: stage_id/funnel_id.`);
    if (membershipFunnelId !== undefined && stageFunnelId !== membershipFunnelId)
      throw new Error(`${invalidMessage}: stage_id/funnel_id.`);
    safeStageId = stage.id;
  }
  let safeTaskId: string | undefined;
  if (taskId !== undefined) {
    const task = await requireOwnedRemoteRow(
      c,
      "tasks",
      lead.ownerId,
      taskId,
      "task_id",
      "owner_id,lead_id,membership_id",
      invalidMessage,
    );
    const taskLeadId = requiredInputIdentifier(
      requiredRpcString(task.row, "lead_id", invalidMessage),
      "lead_id",
      invalidMessage,
    );
    if (taskLeadId !== lead.leadId) throw new Error(`${invalidMessage}: task_id/lead_id.`);
    const taskMembershipId = optionalRpcString(task.row, "membership_id", invalidMessage);
    if (safeMembershipId !== undefined && taskMembershipId !== safeMembershipId)
      throw new Error(`${invalidMessage}: task_id/membership_id.`);
    safeTaskId = task.id;
  }
  return {
    ownerId: lead.ownerId,
    leadId: lead.leadId,
    ...(safeMembershipId === undefined ? {} : { membershipId: safeMembershipId }),
    ...(safeTaskId === undefined ? {} : { taskId: safeTaskId }),
    ...(safeStageId === undefined ? {} : { stageId: safeStageId }),
  };
}

type ValidatedUploadFile = { name: string; type: string; size: number };

function validateUploadFile(
  file: File,
  allowedMimeTypes: readonly string[],
  invalidMessage: string,
): ValidatedUploadFile {
  if (typeof file !== "object" || file === null) throw new Error(invalidMessage);
  let name: unknown;
  let type: unknown;
  let size: unknown;
  try {
    name = file.name;
    type = file.type;
    size = file.size;
  } catch {
    throw new Error(invalidMessage);
  }
  const safeName = requiredStorageSegment(name, "name", invalidMessage);
  if (
    typeof type !== "string" ||
    type.length > 255 ||
    hasStorageControlCharacter(type) ||
    !allowedMimeTypes.includes(type)
  ) {
    throw new Error(`${invalidMessage}: type.`);
  }
  if (typeof size !== "number" || !Number.isSafeInteger(size) || size < 0 || size > maxLeadFileBytes)
    throw new Error(`${invalidMessage}: size.`);
  return { name: safeName, type, size };
}

type RemoteStorageReference = {
  ownerId: string;
  leadId: string;
  bucket: string;
  storagePath: string;
};

function parseRemoteStorageReference(value: unknown, invalidMessage: string): RemoteStorageReference {
  const row = rpcRecord(value, invalidMessage);
  const bucket = optionalRpcString(row, "bucket", invalidMessage) ?? leadFilesBucket;
  if (bucket !== leadFilesBucket) throw new Error(`${invalidMessage}: bucket.`);
  return {
    ownerId: requiredStorageSegment(requiredRpcString(row, "owner_id", invalidMessage), "owner_id", invalidMessage),
    leadId: requiredStorageSegment(requiredRpcString(row, "lead_id", invalidMessage), "lead_id", invalidMessage),
    bucket,
    storagePath: requiredStoragePath(rpcField(row, "storage_path", invalidMessage), "storage_path", invalidMessage),
  };
}

function inputKeys(payload: RpcRecord, invalidMessage: string): string[] {
  try {
    return Object.keys(payload);
  } catch {
    throw new Error(invalidMessage);
  }
}

function inputHasField(payload: RpcRecord, key: string, invalidMessage: string): boolean {
  try {
    return Object.prototype.hasOwnProperty.call(payload, key);
  } catch {
    throw new Error(invalidMessage);
  }
}

function assertAllowedInputKeys(payload: RpcRecord, allowed: readonly string[], invalidMessage: string): void {
  for (const key of inputKeys(payload, invalidMessage)) {
    if (!allowed.includes(key)) throw new Error(`${invalidMessage}: ${key}.`);
  }
}

function requiredInputIdentifier(value: unknown, key: string, invalidMessage: string): string {
  const normalized = typeof value === "string" ? value.trim() : value;
  return requiredStorageSegment(normalized, key, invalidMessage);
}

function requiredInputIdentifierArray(value: unknown, key: string, invalidMessage: string): string[] {
  if (!isDenseRpcArray(value)) throw new Error(`${invalidMessage}: ${key}.`);
  const normalized = value.map((item) => requiredInputIdentifier(item, key, invalidMessage));
  if (new Set(normalized).size !== normalized.length) throw new Error(`${invalidMessage}: ${key}.`);
  return normalized;
}

function requiredInputText(value: unknown, key: string, invalidMessage: string): string {
  if (typeof value !== "string" || !value.trim()) throw new Error(`${invalidMessage}: ${key}.`);
  return value.trim();
}

function inputTextValue(value: unknown, key: string, invalidMessage: string): string {
  if (typeof value !== "string") throw new Error(`${invalidMessage}: ${key}.`);
  return value.trim();
}

function requiredInputUrl(value: unknown, key: string, invalidMessage: string): string {
  const normalized = requiredInputText(value, key, invalidMessage);
  let parsed: URL;
  try {
    parsed = new URL(normalized);
  } catch {
    throw new Error(`${invalidMessage}: ${key}.`);
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") throw new Error(`${invalidMessage}: ${key}.`);
  return normalized;
}

function optionalInputText(payload: RpcRecord, key: string, invalidMessage: string): string | null {
  const value = rpcField(payload, key, invalidMessage);
  if (value === null || value === undefined) return null;
  if (typeof value !== "string") throw new Error(`${invalidMessage}: ${key}.`);
  const normalized = value.trim();
  return normalized || null;
}

function optionalInputIdentifier(payload: RpcRecord, key: string, invalidMessage: string): string | undefined {
  const value = rpcField(payload, key, invalidMessage);
  if (value === null || value === undefined) return undefined;
  return requiredInputIdentifier(value, key, invalidMessage);
}

function optionalInputIdentifierOrBlank(payload: RpcRecord, key: string, invalidMessage: string): string | undefined {
  const value = rpcField(payload, key, invalidMessage);
  if (value === null || value === undefined) return undefined;
  if (typeof value !== "string") throw new Error(`${invalidMessage}: ${key}.`);
  if (!value.trim()) return undefined;
  return requiredInputIdentifier(value, key, invalidMessage);
}

function optionalInputIdentifierArray(payload: RpcRecord, key: string, invalidMessage: string): string[] | undefined {
  const value = rpcField(payload, key, invalidMessage);
  if (value === undefined) return undefined;
  if (!isDenseRpcArray(value)) throw new Error(`${invalidMessage}: ${key}.`);
  const normalized: string[] = [];
  for (let index = 0; index < value.length; index += 1)
    normalized.push(requiredInputIdentifier(value[index], key, invalidMessage));
  if (new Set(normalized).size !== normalized.length) throw new Error(`${invalidMessage}: ${key}.`);
  return normalized;
}

function requiredInputNonNegativeNumber(value: unknown, key: string, invalidMessage: string): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value > 999_999_999_999.99)
    throw new Error(`${invalidMessage}: ${key}.`);
  return value;
}

function requiredInputTimestamp(value: unknown, key: string, invalidMessage: string): string {
  const normalized = requiredInputText(value, key, invalidMessage);
  if (!Number.isFinite(Date.parse(normalized))) throw new Error(`${invalidMessage}: ${key}.`);
  return normalized;
}

function optionalInputBoolean(payload: RpcRecord, key: string, invalidMessage: string): boolean | undefined {
  const value = rpcField(payload, key, invalidMessage);
  if (value === undefined) return undefined;
  if (typeof value !== "boolean") throw new Error(`${invalidMessage}: ${key}.`);
  return value;
}

function parseRemoteActivityEventInput(value: unknown): RemoteActivityEventInput {
  const invalidMessage = "O evento para persistência é inválido.";
  const payload = rpcRecord(value, invalidMessage);
  assertAllowedInputKeys(payload, [
    "id", "leadId", "membershipId", "taskId", "type", "title", "description", "occurredAt", "stageId",
    "countsAsInteraction", "kind",
  ], invalidMessage);
  const kind = rpcField(payload, "kind", invalidMessage);
  if (kind !== undefined && kind !== "event") throw new Error(`${invalidMessage}: kind.`);
  optionalInputBoolean(payload, "countsAsInteraction", invalidMessage);
  const description = optionalInputText(payload, "description", invalidMessage);
  const membershipId = optionalInputIdentifier(payload, "membershipId", invalidMessage);
  const taskId = optionalInputIdentifier(payload, "taskId", invalidMessage);
  const stageId = optionalInputIdentifier(payload, "stageId", invalidMessage);
  return {
    id: requiredInputIdentifier(rpcField(payload, "id", invalidMessage), "id", invalidMessage),
    leadId: requiredInputIdentifier(rpcField(payload, "leadId", invalidMessage), "leadId", invalidMessage),
    type: requiredInputText(rpcField(payload, "type", invalidMessage), "type", invalidMessage),
    title: requiredInputText(rpcField(payload, "title", invalidMessage), "title", invalidMessage),
    occurredAt: requiredInputTimestamp(rpcField(payload, "occurredAt", invalidMessage), "occurredAt", invalidMessage),
    ...(description === null ? {} : { description }),
    ...(membershipId === undefined ? {} : { membershipId }),
    ...(taskId === undefined ? {} : { taskId }),
    ...(stageId === undefined ? {} : { stageId }),
  };
}

type RemoteLeadUpdateInput = {
  leadId: string;
  row: Record<string, unknown>;
  tagIds?: string[];
  triggerIds?: string[];
};

const leadPriorities = ["low", "medium", "high"] as const;

type RemoteLeadCreateInput = {
  leadId: string;
  funnelId: string;
  stageId: string;
  row: Record<string, unknown>;
  tagIds: string[];
  triggerIds: string[];
};

function parseRemoteLeadCreateInput(
  value: unknown,
  funnelIdValue: unknown,
  stageIdValue: unknown,
): RemoteLeadCreateInput {
  const invalidMessage = "O lead para persistência é inválido.";
  const payload = rpcRecord(value, invalidMessage);
  assertAllowedInputKeys(payload, [
    "id", "companyId", "primaryContactId", "name", "company", "role", "potentialValue", "email", "phone",
    "site", "instagram", "biggestPain", "biggestObjection", "bestOverdelivery", "city", "state", "priority",
    "segment", "source", "tagIds", "triggerIds", "createdAt",
  ], invalidMessage);
  const leadId = requiredInputIdentifier(rpcField(payload, "id", invalidMessage), "id", invalidMessage);
  const funnelId = requiredInputIdentifier(funnelIdValue, "funnel_id", invalidMessage);
  const stageId = requiredInputIdentifier(stageIdValue, "stage_id", invalidMessage);
  const companyId = optionalInputIdentifierOrBlank(payload, "companyId", invalidMessage);
  const primaryContactId = optionalInputIdentifierOrBlank(payload, "primaryContactId", invalidMessage);
  if (primaryContactId !== undefined && companyId === undefined)
    throw new Error(`${invalidMessage}: primaryContactId sem companyId.`);
  if (inputHasField(payload, "createdAt", invalidMessage))
    requiredInputTimestamp(rpcField(payload, "createdAt", invalidMessage), "createdAt", invalidMessage);
  const tagIds = optionalInputIdentifierArray(payload, "tagIds", invalidMessage) ?? [];
  const triggerIds = optionalInputIdentifierArray(payload, "triggerIds", invalidMessage) ?? [];
  return {
    leadId,
    funnelId,
    stageId,
    row: {
      id: leadId,
      name: requiredInputText(rpcField(payload, "name", invalidMessage), "name", invalidMessage),
      company: requiredInputText(rpcField(payload, "company", invalidMessage), "company", invalidMessage),
      role: requiredInputText(rpcField(payload, "role", invalidMessage), "role", invalidMessage),
      potential_value: requiredInputNonNegativeNumber(rpcField(payload, "potentialValue", invalidMessage), "potentialValue", invalidMessage),
      email: optionalInputText(payload, "email", invalidMessage),
      phone: optionalInputText(payload, "phone", invalidMessage),
      site: optionalInputText(payload, "site", invalidMessage),
      instagram: optionalInputText(payload, "instagram", invalidMessage),
      biggest_pain: optionalInputText(payload, "biggestPain", invalidMessage),
      biggest_objection: optionalInputText(payload, "biggestObjection", invalidMessage),
      best_overdelivery: optionalInputText(payload, "bestOverdelivery", invalidMessage),
      city: optionalInputText(payload, "city", invalidMessage),
      state: optionalInputText(payload, "state", invalidMessage),
      priority: requiredRpcEnum(payload, "priority", leadPriorities, invalidMessage),
      segment: requiredInputText(rpcField(payload, "segment", invalidMessage), "segment", invalidMessage),
      source: requiredInputText(rpcField(payload, "source", invalidMessage), "source", invalidMessage),
      ...(companyId === undefined ? {} : {
        company_id: companyId,
        funnel_id: funnelId,
        primary_contact_id: primaryContactId ?? null,
      }),
    },
    tagIds,
    triggerIds,
  };
}

function parseRemoteLeadUpdateInput(id: unknown, value: unknown): RemoteLeadUpdateInput {
  const invalidMessage = "A atualização do lead é inválida.";
  const payload = rpcRecord(value, invalidMessage);
  assertAllowedInputKeys(payload, [
    "name", "company", "role", "potentialValue", "email", "phone", "site", "instagram",
    "biggestPain", "biggestObjection", "bestOverdelivery", "city", "state", "priority", "segment",
    "source", "tagIds", "triggerIds",
  ], invalidMessage);
  const row: Record<string, unknown> = {};
  if (inputHasField(payload, "name", invalidMessage))
    row.name = requiredInputText(rpcField(payload, "name", invalidMessage), "name", invalidMessage);
  if (inputHasField(payload, "company", invalidMessage))
    row.company = optionalInputText(payload, "company", invalidMessage);
  if (inputHasField(payload, "role", invalidMessage))
    row.role = optionalInputText(payload, "role", invalidMessage);
  if (inputHasField(payload, "potentialValue", invalidMessage))
    row.potential_value = requiredInputNonNegativeNumber(rpcField(payload, "potentialValue", invalidMessage), "potentialValue", invalidMessage);
  if (inputHasField(payload, "email", invalidMessage))
    row.email = optionalInputText(payload, "email", invalidMessage);
  if (inputHasField(payload, "phone", invalidMessage))
    row.phone = optionalInputText(payload, "phone", invalidMessage);
  if (inputHasField(payload, "site", invalidMessage))
    row.site = optionalInputText(payload, "site", invalidMessage);
  if (inputHasField(payload, "instagram", invalidMessage))
    row.instagram = optionalInputText(payload, "instagram", invalidMessage);
  if (inputHasField(payload, "biggestPain", invalidMessage))
    row.biggest_pain = optionalInputText(payload, "biggestPain", invalidMessage);
  if (inputHasField(payload, "biggestObjection", invalidMessage))
    row.biggest_objection = optionalInputText(payload, "biggestObjection", invalidMessage);
  if (inputHasField(payload, "bestOverdelivery", invalidMessage))
    row.best_overdelivery = optionalInputText(payload, "bestOverdelivery", invalidMessage);
  if (inputHasField(payload, "city", invalidMessage))
    row.city = optionalInputText(payload, "city", invalidMessage);
  if (inputHasField(payload, "state", invalidMessage))
    row.state = optionalInputText(payload, "state", invalidMessage);
  if (inputHasField(payload, "priority", invalidMessage))
    row.priority = requiredRpcEnum(payload, "priority", leadPriorities, invalidMessage);
  if (inputHasField(payload, "segment", invalidMessage))
    row.segment = optionalInputText(payload, "segment", invalidMessage);
  if (inputHasField(payload, "source", invalidMessage))
    row.source = optionalInputText(payload, "source", invalidMessage);
  const tagIds = optionalInputIdentifierArray(payload, "tagIds", invalidMessage);
  const triggerIds = optionalInputIdentifierArray(payload, "triggerIds", invalidMessage);
  const leadId = requiredInputIdentifier(id, "lead_id", invalidMessage);
  if (!Object.keys(row).length && tagIds === undefined && triggerIds === undefined)
    throw new Error(invalidMessage);
  return { leadId, row, ...(tagIds === undefined ? {} : { tagIds }), ...(triggerIds === undefined ? {} : { triggerIds }) };
}

type FormSectionInput = { id: string; funnelId: string; name: string; order: number; archived?: boolean };

function parseFormSectionInput(value: unknown): FormSectionInput {
  const invalidMessage = "A seção de formulário para persistência é inválida.";
  const payload = rpcRecord(value, invalidMessage);
  assertAllowedInputKeys(payload, ["id", "funnelId", "name", "order", "archived"], invalidMessage);
  const archived = optionalInputBoolean(payload, "archived", invalidMessage);
  return {
    id: requiredInputIdentifier(rpcField(payload, "id", invalidMessage), "id", invalidMessage),
    funnelId: requiredInputIdentifier(rpcField(payload, "funnelId", invalidMessage), "funnelId", invalidMessage),
    name: requiredInputText(rpcField(payload, "name", invalidMessage), "name", invalidMessage),
    order: requiredRpcSafeInteger(payload, "order", 0, invalidMessage),
    ...(archived === undefined ? {} : { archived }),
  };
}

type LeadFieldInput = {
  id: string;
  funnelId: string;
  sectionId: string;
  name: string;
  key: string;
  type: LeadFieldType;
  order: number;
  width: LeadFieldWidth;
  height: LeadFieldHeight;
  required: boolean;
  inForm: boolean;
  inMetrics: boolean;
  placeholder: string | null;
  helpText: string | null;
  archived?: boolean;
};

function parseLeadFieldInput(value: unknown): LeadFieldInput {
  const invalidMessage = "A definição de campo para persistência é inválida.";
  const payload = rpcRecord(value, invalidMessage);
  assertAllowedInputKeys(payload, [
    "id", "funnelId", "sectionId", "name", "key", "type", "order", "width", "height",
    "required", "inForm", "inMetrics", "placeholder", "helpText", "archived",
  ], invalidMessage);
  const key = requiredInputText(rpcField(payload, "key", invalidMessage), "key", invalidMessage);
  if (!/^[a-z][a-z0-9_]*$/.test(key)) throw new Error(`${invalidMessage}: key.`);
  const archived = optionalInputBoolean(payload, "archived", invalidMessage);
  return {
    id: requiredInputIdentifier(rpcField(payload, "id", invalidMessage), "id", invalidMessage),
    funnelId: requiredInputIdentifier(rpcField(payload, "funnelId", invalidMessage), "funnelId", invalidMessage),
    sectionId: requiredInputIdentifier(rpcField(payload, "sectionId", invalidMessage), "sectionId", invalidMessage),
    name: requiredInputText(rpcField(payload, "name", invalidMessage), "name", invalidMessage),
    key,
    type: requiredRpcEnum(payload, "type", leadFieldTypes, invalidMessage),
    order: requiredRpcSafeInteger(payload, "order", 0, invalidMessage),
    width: requiredRpcEnum(payload, "width", leadFieldWidths, invalidMessage),
    height: requiredRpcEnum(payload, "height", leadFieldHeights, invalidMessage),
    required: requiredRpcBoolean(payload, "required", invalidMessage),
    inForm: requiredRpcBoolean(payload, "inForm", invalidMessage),
    inMetrics: optionalInputBoolean(payload, "inMetrics", invalidMessage) ?? false,
    placeholder: optionalInputText(payload, "placeholder", invalidMessage),
    helpText: optionalInputText(payload, "helpText", invalidMessage),
    ...(archived === undefined ? {} : { archived }),
  };
}

type LeadFieldOptionInput = { id: string; fieldId: string; name: string; color: string | null; order: number; archived?: boolean };

function parseLeadFieldOptionInput(value: unknown): LeadFieldOptionInput {
  const invalidMessage = "A opção de campo para persistência é inválida.";
  const payload = rpcRecord(value, invalidMessage);
  assertAllowedInputKeys(payload, ["id", "fieldId", "name", "color", "order", "archived"], invalidMessage);
  const archived = optionalInputBoolean(payload, "archived", invalidMessage);
  return {
    id: requiredInputIdentifier(rpcField(payload, "id", invalidMessage), "id", invalidMessage),
    fieldId: requiredInputIdentifier(rpcField(payload, "fieldId", invalidMessage), "fieldId", invalidMessage),
    name: requiredInputText(rpcField(payload, "name", invalidMessage), "name", invalidMessage),
    color: optionalInputText(payload, "color", invalidMessage),
    order: requiredRpcSafeInteger(payload, "order", 0, invalidMessage),
    ...(archived === undefined ? {} : { archived }),
  };
}

type LeadLinkInput = { id: string; leadId: string; label: string; url: string; createdAt?: string };

function parseLeadLinkInput(value: unknown): LeadLinkInput {
  const invalidMessage = "O link para persistência é inválido.";
  const payload = rpcRecord(value, invalidMessage);
  assertAllowedInputKeys(payload, ["id", "leadId", "label", "url", "createdAt"], invalidMessage);
  const createdAt = inputHasField(payload, "createdAt", invalidMessage)
    ? requiredInputText(rpcField(payload, "createdAt", invalidMessage), "createdAt", invalidMessage)
    : undefined;
  return {
    id: requiredInputIdentifier(rpcField(payload, "id", invalidMessage), "id", invalidMessage),
    leadId: requiredInputIdentifier(rpcField(payload, "leadId", invalidMessage), "leadId", invalidMessage),
    label: requiredInputText(rpcField(payload, "label", invalidMessage), "label", invalidMessage),
    url: requiredInputUrl(rpcField(payload, "url", invalidMessage), "url", invalidMessage),
    ...(createdAt === undefined ? {} : { createdAt }),
  };
}

const configWriteTables = ["funnels", "stages", "tags", "triggers", "loss_reasons"] as const;
const archiveTables = ["leads", ...configWriteTables] as const;
type ConfigWriteTable = (typeof configWriteTables)[number];
type ArchiveTable = (typeof archiveTables)[number];

function requiredTable<T extends string>(value: unknown, allowed: readonly T[], invalidMessage: string): T {
  if (typeof value !== "string" || !allowed.includes(value as T)) throw new Error(invalidMessage);
  return value as T;
}

function parseConfigWrite(table: unknown, value: unknown): { table: ConfigWriteTable; row: Record<string, unknown> } {
  const invalidMessage = "A configuração para persistência é inválida.";
  const payload = rpcRecord(value, invalidMessage);
  const safeTable = requiredTable(table, configWriteTables, invalidMessage);
  if (safeTable === "funnels") {
    assertAllowedInputKeys(payload, ["id", "name", "description", "position"], invalidMessage);
    return {
      table: safeTable,
      row: {
        id: requiredInputIdentifier(rpcField(payload, "id", invalidMessage), "id", invalidMessage),
        name: requiredInputText(rpcField(payload, "name", invalidMessage), "name", invalidMessage),
        description: inputTextValue(rpcField(payload, "description", invalidMessage), "description", invalidMessage),
        position: requiredRpcSafeInteger(payload, "position", 0, invalidMessage),
      },
    };
  }
  if (safeTable === "stages") {
    assertAllowedInputKeys(payload, ["id", "funnel_id", "name", "position", "color"], invalidMessage);
    return {
      table: safeTable,
      row: {
        id: requiredInputIdentifier(rpcField(payload, "id", invalidMessage), "id", invalidMessage),
        funnel_id: requiredInputIdentifier(rpcField(payload, "funnel_id", invalidMessage), "funnel_id", invalidMessage),
        name: requiredInputText(rpcField(payload, "name", invalidMessage), "name", invalidMessage),
        position: requiredRpcSafeInteger(payload, "position", 0, invalidMessage),
        color: requiredInputText(rpcField(payload, "color", invalidMessage), "color", invalidMessage),
      },
    };
  }
  if (safeTable === "tags" || safeTable === "triggers") {
    assertAllowedInputKeys(payload, ["id", "funnel_id", "name", "color"], invalidMessage);
    return {
      table: safeTable,
      row: {
        id: requiredInputIdentifier(rpcField(payload, "id", invalidMessage), "id", invalidMessage),
        funnel_id: requiredInputIdentifier(rpcField(payload, "funnel_id", invalidMessage), "funnel_id", invalidMessage),
        name: requiredInputText(rpcField(payload, "name", invalidMessage), "name", invalidMessage),
        color: requiredInputText(rpcField(payload, "color", invalidMessage), "color", invalidMessage),
      },
    };
  }
  assertAllowedInputKeys(payload, ["id", "funnel_id", "name", "position"], invalidMessage);
  return {
    table: safeTable,
    row: {
      id: requiredInputIdentifier(rpcField(payload, "id", invalidMessage), "id", invalidMessage),
      funnel_id: requiredInputIdentifier(rpcField(payload, "funnel_id", invalidMessage), "funnel_id", invalidMessage),
      name: requiredInputText(rpcField(payload, "name", invalidMessage), "name", invalidMessage),
      position: requiredRpcSafeInteger(payload, "position", 0, invalidMessage),
    },
  };
}

function parseTaskImmutableReference(
  value: unknown,
  invalidMessage: string,
): Task["immutableReference"] {
  if (value === null || value === undefined) return undefined;
  const reference = rpcRecord(value, `${invalidMessage}: immutableReference.`);
  const runId = optionalRpcString(reference, "runId", invalidMessage);
  const runVersion = optionalRpcSafeInteger(reference, "runVersion", 1, invalidMessage);
  const stepId = optionalRpcString(reference, "stepId", invalidMessage);
  const stepVersion = optionalRpcSafeInteger(reference, "stepVersion", 1, invalidMessage);
  const blocking = optionalRpcBoolean(reference, "blocking", invalidMessage);
  return {
    type: requiredRpcString(reference, "type", invalidMessage),
    id: requiredRpcString(reference, "id", invalidMessage),
    version: requiredRpcSafeInteger(reference, "version", 1, invalidMessage),
    ...(runId === undefined ? {} : { runId }),
    ...(runVersion === undefined ? {} : { runVersion }),
    ...(stepId === undefined ? {} : { stepId }),
    ...(stepVersion === undefined ? {} : { stepVersion }),
    ...(blocking === undefined ? {} : { blocking }),
  };
}

function parseTaskCommandTask(
  value: unknown,
  transition: (typeof taskTransitions)[number],
  invalidMessage: string,
): Task {
  const payload = rpcRecord(value, `${invalidMessage}: value.task.`);
  const status = requiredRpcEnum(payload, "status", taskStatuses, invalidMessage);
  const completedAt = optionalRpcString(payload, "completedAt", invalidMessage);
  if (transition === "completed" && (status !== "done" || !completedAt))
    throw new Error(`${invalidMessage}: value.task.status.`);
  if (transition !== "completed" && (status !== "pending" || completedAt))
    throw new Error(`${invalidMessage}: value.task.status.`);
  return {
    id: requiredRpcString(payload, "id", invalidMessage),
    leadId: requiredRpcString(payload, "leadId", invalidMessage),
    membershipId: optionalRpcString(payload, "membershipId", invalidMessage),
    description: requiredRpcString(payload, "description", invalidMessage),
    dueAt: requiredRpcString(payload, "dueAt", invalidMessage),
    priority: requiredRpcEnum(payload, "priority", taskPriorities, invalidMessage),
    status,
    progress: optionalRpcEnum(payload, "progress", taskProgressValues, invalidMessage) ?? "todo",
    note: optionalRpcString(payload, "note", invalidMessage),
    completedAt,
    createdAt: requiredRpcString(payload, "createdAt", invalidMessage),
    updatedAt: requiredRpcString(payload, "updatedAt", invalidMessage),
    version: requiredRpcSafeInteger(payload, "version", 1, invalidMessage),
    origin: requiredRpcEnum(payload, "origin", taskOrigins, invalidMessage),
    actionType: requiredRpcEnum(payload, "actionType", taskActionTypes, invalidMessage),
    sensitive: requiredRpcBoolean(payload, "sensitive", invalidMessage),
    immutableReference: parseTaskImmutableReference(rpcField(payload, "immutableReference", invalidMessage), invalidMessage),
  };
}

function parseTaskCommandEvent(
  value: unknown,
  transition: (typeof taskTransitions)[number],
  task: Task,
  commandId: string,
  correlationId: string,
  invalidMessage: string,
): ActivityEvent {
  const payload = rpcRecord(value, `${invalidMessage}: value.event.`);
  const type = requiredRpcString(payload, "type", invalidMessage);
  if (type !== `task_${transition}`) throw new Error(`${invalidMessage}: value.event.type.`);
  const membershipId = optionalRpcString(payload, "membershipId", invalidMessage);
  if (membershipId !== task.membershipId)
    throw new Error(`${invalidMessage}: value.event.membershipId.`);
  const eventCommandId = optionalRpcString(payload, "commandId", invalidMessage);
  if (eventCommandId !== undefined && eventCommandId !== commandId)
    throw new Error(`${invalidMessage}: value.event.commandId.`);
  const eventCorrelationId = optionalRpcString(payload, "correlationId", invalidMessage);
  if (eventCorrelationId !== undefined && eventCorrelationId !== correlationId)
    throw new Error(`${invalidMessage}: value.event.correlationId.`);
  const countsAsInteraction = optionalRpcBoolean(payload, "countsAsInteraction", invalidMessage);
  if (countsAsInteraction === true) throw new Error(`${invalidMessage}: value.event.countsAsInteraction.`);
  const eventId = requiredRpcString(payload, "id", invalidMessage);
  const leadId = requiredRpcString(payload, "leadId", invalidMessage);
  if (leadId !== task.leadId) throw new Error(`${invalidMessage}: value.event.leadId.`);
  const taskId = requiredRpcString(payload, "taskId", invalidMessage);
  if (taskId !== task.id) throw new Error(`${invalidMessage}: value.event.taskId.`);
  const previousStatus = optionalRpcString(payload, "previousStatus", invalidMessage);
  if (previousStatus !== undefined && !membershipStatuses.includes(previousStatus as (typeof membershipStatuses)[number]))
    throw new Error(`${invalidMessage}: value.event.previousStatus.`);
  const actorType = optionalRpcString(payload, "actorType", invalidMessage);
  if (actorType !== undefined && !commandActorTypes.includes(actorType as (typeof commandActorTypes)[number]))
    throw new Error(`${invalidMessage}: value.event.actorType.`);
  const origin = optionalRpcString(payload, "origin", invalidMessage);
  if (origin !== undefined && !commandOrigins.includes(origin as (typeof commandOrigins)[number]))
    throw new Error(`${invalidMessage}: value.event.origin.`);
  return {
    id: eventId,
    leadId,
    membershipId,
    taskId,
    type,
    title: requiredRpcString(payload, "title", invalidMessage),
    description: optionalRpcString(payload, "description", invalidMessage),
    occurredAt: requiredRpcString(payload, "occurredAt", invalidMessage),
    stageId: optionalRpcString(payload, "stageId", invalidMessage),
    fromStageId: optionalRpcString(payload, "fromStageId", invalidMessage),
    lossReasonId: optionalRpcString(payload, "lossReasonId", invalidMessage),
    previousStatus: previousStatus as ActivityEvent["previousStatus"],
    previousLossReasonId: optionalRpcString(payload, "previousLossReasonId", invalidMessage),
    kind: requiredRpcEnum(payload, "kind", ["event"] as const, invalidMessage),
    durationLabel: optionalRpcString(payload, "durationLabel", invalidMessage),
    actorType: actorType as ActivityEvent["actorType"],
    actorId: optionalRpcString(payload, "actorId", invalidMessage),
    origin: origin as ActivityEvent["origin"],
    commandId: eventCommandId,
    correlationId: eventCorrelationId,
    causationId: optionalRpcString(payload, "causationId", invalidMessage),
    countsAsInteraction,
  };
}

function parseTaskCommandError(
  value: unknown,
  correlationId: string,
  invalidMessage: string,
): CommandError {
  const payload = rpcRecord(value, `${invalidMessage}: error.`);
  const code = requiredRpcEnum(payload, "code", commandResultErrorCodes, invalidMessage);
  const errorCorrelationId = requiredRpcString(payload, "correlationId", invalidMessage);
  if (errorCorrelationId !== correlationId)
    throw new Error(`${invalidMessage}: error.correlationId.`);
  const sanitizedCause = optionalRpcString(payload, "sanitizedCause", invalidMessage);
  const details = optionalRpcDetails(payload, "details", invalidMessage);
  return {
    code,
    message: requiredRpcString(payload, "message", invalidMessage),
    retryable: requiredRpcBoolean(payload, "retryable", invalidMessage),
    correlationId: errorCorrelationId,
    severity: requiredRpcEnum(payload, "severity", commandResultSeverities, invalidMessage),
    ...(sanitizedCause === undefined ? {} : { sanitizedCause }),
    ...(details === undefined ? {} : { details }),
  };
}

function failureStatusForCode(code: (typeof commandResultErrorCodes)[number]): "rejected" | "conflict" | "failed" {
  if (code === "idempotency_conflict" || code === "version_conflict") return "conflict";
  if (code === "validation_error" || code === "invalid_state") return "rejected";
  return "failed";
}

export function parseTaskCommandResult(value: unknown): CommandResult<TaskCommandValue> {
  const invalidMessage = "A RPC de tarefa devolveu um resultado inválido.";
  const payload = rpcRecord(value, invalidMessage);
  const ok = rpcField(payload, "ok", invalidMessage);
  if (typeof ok !== "boolean") throw new Error(`${invalidMessage}: ok.`);
  const status = requiredRpcString(payload, "status", invalidMessage);
  const commandId = requiredRpcString(payload, "commandId", invalidMessage);
  const correlationId = requiredRpcString(payload, "correlationId", invalidMessage);
  const eventIds = requiredRpcStringArray(payload, "eventIds", invalidMessage);
  const outboxJobIds = requiredRpcStringArray(payload, "outboxJobIds", invalidMessage);
  const warnings = requiredRpcStringArray(payload, "warnings", invalidMessage);

  if (ok) {
    if (status !== "applied" && status !== "replayed") throw new Error(`${invalidMessage}: status.`);
    const version = requiredRpcSafeInteger(payload, "version", 1, invalidMessage);
    const transitionPayload = rpcRecord(rpcField(payload, "value", invalidMessage), `${invalidMessage}: value.`);
    const transition = requiredRpcEnum(transitionPayload, "transition", taskTransitions, invalidMessage);
    const task = parseTaskCommandTask(rpcField(transitionPayload, "task", invalidMessage), transition, invalidMessage);
    const event = parseTaskCommandEvent(
      rpcField(transitionPayload, "event", invalidMessage),
      transition,
      task,
      commandId,
      correlationId,
      invalidMessage,
    );
    if (task.version !== version) throw new Error(`${invalidMessage}: version.`);
    if (!eventIds.includes(event.id)) throw new Error(`${invalidMessage}: eventIds.`);
    const auditId = requiredRpcString(payload, "auditId", invalidMessage);
    return {
      ok: true,
      status,
      value: { task, event, transition },
      commandId,
      correlationId,
      version,
      eventIds,
      outboxJobIds,
      auditId,
      warnings,
    };
  }

  if (status !== "rejected" && status !== "conflict" && status !== "failed")
    throw new Error(`${invalidMessage}: status.`);
  const error = parseTaskCommandError(rpcField(payload, "error", invalidMessage), correlationId, invalidMessage);
  if (failureStatusForCode(error.code) !== status) throw new Error(`${invalidMessage}: status.`);
  const version = optionalRpcSafeInteger(payload, "version", 0, invalidMessage);
  const auditId = optionalRpcString(payload, "auditId", invalidMessage);
  return {
    ok: false,
    status,
    error,
    commandId,
    correlationId,
    ...(version === undefined ? {} : { version }),
    eventIds,
    outboxJobIds,
    ...(auditId === undefined ? {} : { auditId }),
    warnings,
  };
}

function parseRemoteTaskRow(value: unknown): Task {
  const invalidMessage = "Uma linha remota de tarefa é inválida.";
  const row = rpcRecord(value, invalidMessage);
  const dueAt = requiredRpcString(row, "due_at", invalidMessage);
  const createdAt = optionalRpcString(row, "created_at", invalidMessage) ?? dueAt;
  const updatedAt = optionalRpcString(row, "updated_at", invalidMessage) ?? createdAt;
  const originValue = optionalRpcString(row, "origin", invalidMessage);
  if (originValue !== undefined && !taskOrigins.includes(originValue as (typeof taskOrigins)[number]))
    throw new Error(`${invalidMessage}: origin.`);
  const actionTypeValue = optionalRpcString(row, "action_type", invalidMessage);
  if (actionTypeValue !== undefined && !taskActionTypes.includes(actionTypeValue as (typeof taskActionTypes)[number]))
    throw new Error(`${invalidMessage}: action_type.`);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    leadId: requiredRpcString(row, "lead_id", invalidMessage),
    membershipId: optionalRpcString(row, "membership_id", invalidMessage),
    description: requiredRpcString(row, "description", invalidMessage),
    dueAt,
    priority: requiredRpcEnum(row, "priority", taskPriorities, invalidMessage),
    status: requiredRpcEnum(row, "status", taskStatuses, invalidMessage),
    progress: optionalRpcEnum(row, "progress", taskProgressValues, invalidMessage) ?? "todo",
    note: optionalRpcString(row, "note", invalidMessage),
    completedAt: optionalRpcString(row, "completed_at", invalidMessage),
    createdAt,
    updatedAt,
    version: optionalRpcSafeInteger(row, "version", 1, invalidMessage) ?? 1,
    origin: (originValue ?? "human") as Task["origin"],
    actionType: (actionTypeValue ?? "manual_follow_up") as Task["actionType"],
    sensitive: optionalRpcBoolean(row, "sensitive", invalidMessage) ?? false,
    immutableReference: parseTaskImmutableReference(
      rpcField(row, "immutable_reference", invalidMessage),
      invalidMessage,
    ),
  };
}

type RemoteLeadRow = {
  id: string;
  funnelId?: string;
  companyId?: string;
  primaryContactId?: string;
  name: string;
  company: string;
  role: string;
  potentialValue: number;
  photoPath?: string;
  email?: string;
  phone?: string;
  site?: string;
  instagram?: string;
  biggestPain?: string;
  biggestObjection?: string;
  bestOverdelivery?: string;
  city?: string;
  state?: string;
  segment: string;
  source: string;
  priority: Task["priority"];
  createdAt: string;
  lastInteractionAt?: string;
  archivedAt?: string;
  mergedIntoId?: string;
};

function parseRemoteLeadRow(value: unknown): RemoteLeadRow {
  const invalidMessage = "Uma linha remota de lead é inválida.";
  const row = rpcRecord(value, invalidMessage);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    funnelId: optionalRpcString(row, "funnel_id", invalidMessage),
    companyId: optionalRpcString(row, "company_id", invalidMessage),
    primaryContactId: optionalRpcString(row, "primary_contact_id", invalidMessage),
    name: requiredRpcString(row, "name", invalidMessage),
    company: optionalRpcText(row, "company", invalidMessage) ?? "",
    role: optionalRpcText(row, "role", invalidMessage) ?? "",
    potentialValue: requiredRpcNonNegativeNumber(row, "potential_value", invalidMessage),
    photoPath: optionalRemoteStoragePath(row, "photo_path", invalidMessage),
    email: optionalRpcText(row, "email", invalidMessage),
    phone: optionalRpcText(row, "phone", invalidMessage),
    site: optionalRpcText(row, "site", invalidMessage),
    instagram: optionalRpcText(row, "instagram", invalidMessage),
    biggestPain: optionalRpcText(row, "biggest_pain", invalidMessage),
    biggestObjection: optionalRpcText(row, "biggest_objection", invalidMessage),
    bestOverdelivery: optionalRpcText(row, "best_overdelivery", invalidMessage),
    city: optionalRpcText(row, "city", invalidMessage),
    state: optionalRpcText(row, "state", invalidMessage),
    segment: optionalRpcText(row, "segment", invalidMessage) ?? "",
    source: optionalRpcText(row, "source", invalidMessage) ?? "",
    priority: requiredRpcEnum(row, "priority", taskPriorities, invalidMessage),
    createdAt: requiredRpcString(row, "created_at", invalidMessage),
    lastInteractionAt: optionalRpcString(row, "last_interaction_at", invalidMessage),
    archivedAt: optionalRpcString(row, "archived_at", invalidMessage),
    mergedIntoId: optionalRpcString(row, "merged_into_id", invalidMessage),
  };
}

function parseRemoteFunnelRow(value: unknown): Funnel {
  const invalidMessage = "Uma linha remota de funil é inválida.";
  const row = rpcRecord(value, invalidMessage);
  const structureType = normalizeFunnelStructureType(rpcField(row, "structure_type", invalidMessage));
  const defaultExecutionMode = normalizeFlowExecutionMode(rpcField(row, "default_execution_mode", invalidMessage));
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    name: requiredRpcString(row, "name", invalidMessage),
    description: optionalRpcText(row, "description", invalidMessage) ?? "",
    order: optionalRpcSafeInteger(row, "position", 0, invalidMessage) ?? 0,
    archived: optionalRpcString(row, "archived_at", invalidMessage) !== undefined,
    structureType,
    defaultExecutionMode,
  };
}

function parseRemoteStageRow(value: unknown): Stage {
  const invalidMessage = "Uma linha remota de etapa é inválida.";
  const row = rpcRecord(value, invalidMessage);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    funnelId: requiredRpcString(row, "funnel_id", invalidMessage),
    name: requiredRpcString(row, "name", invalidMessage),
    order: requiredRpcSafeInteger(row, "position", 0, invalidMessage),
    color: requiredRpcString(row, "color", invalidMessage),
    archived: optionalRpcString(row, "archived_at", invalidMessage) !== undefined,
  };
}

function parseRemoteMembershipRow(value: unknown): Membership {
  const invalidMessage = "Uma linha remota de participação é inválida.";
  const row = rpcRecord(value, invalidMessage);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    leadId: requiredRpcString(row, "lead_id", invalidMessage),
    funnelId: requiredRpcString(row, "funnel_id", invalidMessage),
    stageId: requiredRpcString(row, "current_stage_id", invalidMessage),
    status: requiredRpcEnum(row, "status", membershipStatuses, invalidMessage),
    enteredStageAt: requiredRpcString(row, "updated_at", invalidMessage),
    sortOrder: optionalRpcSafeInteger(row, "sort_order", 0, invalidMessage) ?? 0,
    lossReasonId: optionalRpcString(row, "loss_reason_id", invalidMessage),
    closedAt: optionalRpcString(row, "closed_at", invalidMessage),
  };
}

function parseRemoteTagRow(value: unknown, index: number, defaultFunnelId: string): Tag {
  const invalidMessage = "Uma linha remota de tag é inválida.";
  const row = rpcRecord(value, invalidMessage);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    funnelId: optionalRpcString(row, "funnel_id", invalidMessage) ?? defaultFunnelId,
    name: requiredRpcString(row, "name", invalidMessage),
    color: requiredRpcString(row, "color", invalidMessage),
    order: optionalRpcSafeInteger(row, "position", 0, invalidMessage) ?? index + 1,
    archived: optionalRpcString(row, "archived_at", invalidMessage) !== undefined,
  };
}

function parseRemoteTriggerRow(value: unknown, index: number, defaultFunnelId: string): Trigger {
  const invalidMessage = "Uma linha remota de gatilho é inválida.";
  const row = rpcRecord(value, invalidMessage);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    funnelId: optionalRpcString(row, "funnel_id", invalidMessage) ?? defaultFunnelId,
    name: requiredRpcString(row, "name", invalidMessage),
    color: requiredRpcString(row, "color", invalidMessage),
    order: optionalRpcSafeInteger(row, "position", 0, invalidMessage) ?? index + 1,
    archived: optionalRpcString(row, "archived_at", invalidMessage) !== undefined,
  };
}

function parseRemoteClassificationDefinitionRow(
  value: unknown,
  index: number,
): ClassificationDefinition {
  const invalidMessage = "Uma linha remota de classificação é inválida.";
  const row = rpcRecord(value, invalidMessage);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    name: requiredRpcString(row, "name", invalidMessage),
    key: requiredRpcString(row, "key", invalidMessage),
    order: optionalRpcSafeInteger(row, "position", 0, invalidMessage) ?? index + 1,
    archived: optionalRpcString(row, "archived_at", invalidMessage) !== undefined,
  };
}

function parseRemoteClassificationOptionRow(value: unknown, index: number): ClassificationOption {
  const invalidMessage = "Uma linha remota de opção de classificação é inválida.";
  const row = rpcRecord(value, invalidMessage);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    definitionId: requiredRpcString(row, "definition_id", invalidMessage),
    name: requiredRpcString(row, "name", invalidMessage),
    order: optionalRpcSafeInteger(row, "position", 0, invalidMessage) ?? index + 1,
    archived: optionalRpcString(row, "archived_at", invalidMessage) !== undefined,
  };
}

function parseRemoteNotificationStateRow(value: unknown): NoticeState {
  const invalidMessage = "Uma linha remota de estado de notificação é inválida.";
  const row = rpcRecord(value, invalidMessage);
  return {
    noticeKey: requiredRpcString(row, "notice_key", invalidMessage),
    readAt: optionalRpcString(row, "read_at", invalidMessage),
    resolvedAt: optionalRpcString(row, "resolved_at", invalidMessage),
  };
}

function parseRemoteUserPreferenceRow(value: unknown): UserPreference {
  const invalidMessage = "Uma linha remota de preferência é inválida.";
  const row = rpcRecord(value, invalidMessage);
  // O valor é `jsonb` e só precisa ser um objeto aqui; quem dá sentido a ele é
  // o schema da chave, em `readPreference`.
  const stored = rpcField(row, "value", invalidMessage);
  if (!isRecord(stored)) throw new Error(`${invalidMessage}: value.`);
  return {
    key: requiredRpcString(row, "key", invalidMessage),
    value: stored,
    updatedAt: requiredRpcString(row, "updated_at", invalidMessage),
    version: requiredRpcSafeInteger(row, "version", 1, invalidMessage),
  };
}

function parseRemoteMeetingRow(value: unknown): Meeting {
  const invalidMessage = "Uma linha remota de reunião é inválida.";
  const row = rpcRecord(value, invalidMessage);
  const link = optionalRpcText(row, "link", invalidMessage);
  if (link !== undefined && !isAllowedMeetingLink(link))
    throw new Error(`${invalidMessage}: link.`);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    leadId: requiredRpcString(row, "lead_id", invalidMessage),
    membershipId: optionalRpcString(row, "membership_id", invalidMessage),
    title: requiredRpcString(row, "title", invalidMessage),
    startsAt: requiredRpcString(row, "starts_at", invalidMessage),
    durationMinutes: requiredRpcSafeInteger(row, "duration_minutes", 1, invalidMessage),
    participants: optionalRpcStringArray(row, "participants", invalidMessage) ?? [],
    notes: optionalRpcText(row, "notes", invalidMessage),
    link,
    heldAt: optionalRpcString(row, "held_at", invalidMessage),
    cancelledAt: optionalRpcString(row, "cancelled_at", invalidMessage),
    cancelReason: optionalRpcText(row, "cancel_reason", invalidMessage),
    createdAt: requiredRpcString(row, "created_at", invalidMessage),
    updatedAt: requiredRpcString(row, "updated_at", invalidMessage),
    version: requiredRpcSafeInteger(row, "version", 1, invalidMessage),
  };
}

function parseRemoteLeadClassificationValueRow(value: unknown): LeadClassificationValue {
  const invalidMessage = "Uma linha remota de classificação do lead é inválida.";
  const row = rpcRecord(value, invalidMessage);
  return {
    leadId: requiredRpcString(row, "lead_id", invalidMessage),
    definitionId: requiredRpcString(row, "definition_id", invalidMessage),
    optionId: requiredRpcString(row, "option_id", invalidMessage),
  };
}

function parseRemoteLossReasonRow(value: unknown, index: number, defaultFunnelId: string): LossReason {
  const invalidMessage = "Uma linha remota de motivo de perda é inválida.";
  const row = rpcRecord(value, invalidMessage);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    funnelId: optionalRpcString(row, "funnel_id", invalidMessage) ?? defaultFunnelId,
    name: requiredRpcString(row, "name", invalidMessage),
    order: optionalRpcSafeInteger(row, "position", 0, invalidMessage) ?? index + 1,
    archived: optionalRpcString(row, "archived_at", invalidMessage) !== undefined,
  };
}

function optionalRpcStringArray(payload: RpcRecord, key: string, invalidMessage: string): string[] | undefined {
  const value = rpcField(payload, key, invalidMessage);
  if (value === null || value === undefined) return undefined;
  if (!isDenseRpcArray(value)) throw new Error(`${invalidMessage}: ${key}.`);
  return value.map((item) => {
    if (typeof item !== "string" || !item.trim()) throw new Error(`${invalidMessage}: ${key}.`);
    return item.trim();
  });
}

function optionalRpcSorting(
  payload: RpcRecord,
  key: string,
  invalidMessage: string,
): SavedView["sorting"] {
  const value = rpcField(payload, key, invalidMessage);
  if (value === null || value === undefined) return undefined;
  if (!isDenseRpcArray(value)) throw new Error(`${invalidMessage}: ${key}.`);
  return value.map((item) => {
    const entry = rpcRecord(item, `${invalidMessage}: ${key}.`);
    assertAllowedInputKeys(entry, ["id", "desc"], `${invalidMessage}: ${key}.`);
    return {
      id: requiredRpcString(entry, "id", invalidMessage),
      desc: requiredRpcBoolean(entry, "desc", invalidMessage),
    };
  });
}

function parseRemoteSavedViewRow(value: unknown): SavedView {
  const invalidMessage = "Uma linha remota de visualização é inválida.";
  const row = rpcRecord(value, invalidMessage);
  const filters = rpcRecord(rpcField(row, "filters", invalidMessage), `${invalidMessage}: filters.`);
  const schemaVersion = optionalRpcSafeInteger(filters, "schemaVersion", 1, invalidMessage)
    ?? optionalRpcSafeInteger(row, "schema_version", 1, invalidMessage)
    ?? 1;
  const statusValue = optionalRpcString(filters, "status", invalidMessage);
  if (statusValue !== undefined && !membershipStatuses.includes(statusValue as (typeof membershipStatuses)[number]))
    throw new Error(`${invalidMessage}: status.`);
  const priorityValue = optionalRpcString(filters, "priority", invalidMessage);
  if (priorityValue !== undefined && !taskPriorities.includes(priorityValue as (typeof taskPriorities)[number]))
    throw new Error(`${invalidMessage}: priority.`);
  const periodValue = optionalRpcString(filters, "period", invalidMessage);
  if (periodValue !== undefined && !savedViewPeriods.includes(periodValue as (typeof savedViewPeriods)[number]))
    throw new Error(`${invalidMessage}: period.`);
  const nestedColumns = optionalRpcStringArray(filters, "columns", invalidMessage);
  const nestedSorting = optionalRpcSorting(filters, "sorting", invalidMessage);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    schemaVersion,
    name: requiredRpcString(row, "name", invalidMessage),
    query: optionalRpcText(filters, "query", invalidMessage) ?? "",
    funnelId: optionalRpcString(filters, "funnelId", invalidMessage),
    stageId: optionalRpcString(filters, "stageId", invalidMessage),
    status: statusValue as SavedView["status"],
    priority: priorityValue as SavedView["priority"],
    reasonId: optionalRpcString(filters, "reasonId", invalidMessage),
    tagId: optionalRpcString(filters, "tagId", invalidMessage),
    triggerId: optionalRpcString(filters, "triggerId", invalidMessage),
    segment: optionalRpcText(filters, "segment", invalidMessage),
    role: optionalRpcText(filters, "role", invalidMessage),
    source: optionalRpcText(filters, "source", invalidMessage),
    stale: optionalRpcSafeInteger(filters, "stale", 0, invalidMessage),
    period: periodValue,
    periodStart: optionalRpcString(filters, "periodStart", invalidMessage),
    periodEnd: optionalRpcString(filters, "periodEnd", invalidMessage),
    columns: nestedColumns ?? optionalRpcStringArray(row, "columns", invalidMessage),
    sorting: nestedSorting ?? optionalRpcSorting(row, "sorting", invalidMessage),
    // Seleção da lista V2. Opcionais: visualizações gravadas antes delas
    // continuam válidas e abrem na visão padrão.
    view: optionalRpcString(filters, "view", invalidMessage),
    heatBand: optionalRpcString(filters, "heatBand", invalidMessage),
    minValue: optionalRpcSafeInteger(filters, "minValue", 0, invalidMessage),
    maxValue: optionalRpcSafeInteger(filters, "maxValue", 0, invalidMessage),
  };
}

function assertRemoteSavedViewRelationships(
  rows: readonly SavedView[],
  funnelRows: readonly Funnel[],
  stageRows: readonly Stage[],
  lossReasonRows: readonly LossReason[],
  tagRows: readonly Tag[],
  triggerRows: readonly Trigger[],
): void {
  const funnelIds = new Set(funnelRows.map((funnel) => funnel.id));
  const stageById = new Map(stageRows.map((stage) => [stage.id, stage] as const));
  const lossReasonById = new Map(lossReasonRows.map((reason) => [reason.id, reason] as const));
  const tagById = new Map(tagRows.map((tag) => [tag.id, tag] as const));
  const triggerById = new Map(triggerRows.map((trigger) => [trigger.id, trigger] as const));

  for (const view of rows) {
    if (view.funnelId !== undefined && !funnelIds.has(view.funnelId))
      throw new Error("Uma linha remota de visualização é inválida: funnel_id.");
    const references = [
      { id: view.stageId, rows: stageById, key: "stage_id" },
      { id: view.reasonId, rows: lossReasonById, key: "reason_id" },
      { id: view.tagId, rows: tagById, key: "tag_id" },
      { id: view.triggerId, rows: triggerById, key: "trigger_id" },
    ];
    const inferredFunnelIds = new Set<string>();
    for (const reference of references) {
      if (reference.id === undefined) continue;
      const catalog = reference.rows.get(reference.id);
      if (!catalog) throw new Error(`Uma linha remota de visualização é inválida: ${reference.key}.`);
      if (view.funnelId !== undefined && catalog.funnelId !== view.funnelId)
        throw new Error(`Uma linha remota de visualização é inválida: ${reference.key}/funnel_id.`);
      inferredFunnelIds.add(catalog.funnelId);
    }
    if (view.funnelId === undefined && inferredFunnelIds.size > 1)
      throw new Error("Uma linha remota de visualização é inválida: funnel_id.");
  }
}

const savedViewInputKeys = [
  "id", "schemaVersion", "name", "query", "funnelId", "stageId", "status", "priority", "reasonId",
  "tagId", "triggerId", "segment", "role", "source", "stale", "period", "periodStart", "periodEnd",
  "columns", "sorting", "view", "heatBand", "minValue", "maxValue",
] as const;
const savedViewFilterKeys = savedViewInputKeys.filter((key) => key !== "id" && key !== "name") as readonly string[];

function parseSavedViewInput(value: unknown): SavedView {
  const invalidMessage = "A visualização para persistência é inválida.";
  const payload = rpcRecord(value, invalidMessage);
  assertAllowedInputKeys(payload, savedViewInputKeys, invalidMessage);
  const query = rpcField(payload, "query", invalidMessage);
  if (typeof query !== "string") throw new Error(`${invalidMessage}: query.`);
  const filters: RpcRecord = {};
  for (const key of savedViewFilterKeys) {
    const field = rpcField(payload, key, invalidMessage);
    if (field === null) throw new Error(`${invalidMessage}: ${key}.`);
    filters[key] = field;
  }
  return parseRemoteSavedViewRow({
    id: requiredInputIdentifier(rpcField(payload, "id", invalidMessage), "id", invalidMessage),
    name: requiredInputText(rpcField(payload, "name", invalidMessage), "name", invalidMessage),
    filters,
  });
}

type RemoteAttachmentRow = LeadAttachment & { ownerId: string; bucket: string; storagePath: string };

function parseRemoteAttachmentRow(value: unknown): RemoteAttachmentRow {
  const invalidMessage = "Uma linha remota de anexo é inválida.";
  const row = rpcRecord(value, invalidMessage);
  const reference = parseRemoteStorageReference(row, invalidMessage);
  const size = requiredRpcNonNegativeNumber(row, "size_bytes", invalidMessage);
  if (!Number.isSafeInteger(size)) throw new Error(`${invalidMessage}: size_bytes.`);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    ownerId: reference.ownerId,
    leadId: reference.leadId,
    bucket: reference.bucket,
    storagePath: reference.storagePath,
    name: requiredRpcString(row, "filename", invalidMessage),
    type: optionalRpcText(row, "mime_type", invalidMessage) ?? "",
    size,
    createdAt: requiredRpcString(row, "created_at", invalidMessage),
  };
}

function parseRemoteLinkRow(value: unknown): LeadLink {
  const invalidMessage = "Uma linha remota de link é inválida.";
  const row = rpcRecord(value, invalidMessage);
  const url = requiredRpcString(row, "url", invalidMessage);
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url);
  } catch {
    throw new Error(`${invalidMessage}: url.`);
  }
  if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:")
    throw new Error(`${invalidMessage}: url.`);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    leadId: requiredRpcString(row, "lead_id", invalidMessage),
    label: optionalRpcText(row, "label", invalidMessage) ?? url,
    url,
    createdAt: requiredRpcString(row, "created_at", invalidMessage),
  };
}

function parseRemoteFormSectionRow(value: unknown): LeadFormSection {
  const invalidMessage = "Uma linha remota de seção de formulário é inválida.";
  const row = rpcRecord(value, invalidMessage);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    funnelId: requiredRpcString(row, "funnel_id", invalidMessage),
    name: requiredRpcString(row, "name", invalidMessage),
    order: requiredRpcSafeInteger(row, "position", 0, invalidMessage),
    archived: optionalRpcString(row, "archived_at", invalidMessage) !== undefined,
  };
}

function parseRemoteLeadFieldRow(value: unknown): LeadFieldDefinition {
  const invalidMessage = "Uma linha remota de definição de campo é inválida.";
  const row = rpcRecord(value, invalidMessage);
  const key = requiredRpcString(row, "field_key", invalidMessage);
  if (!/^[a-z][a-z0-9_]*$/.test(key)) throw new Error(`${invalidMessage}: field_key.`);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    funnelId: requiredRpcString(row, "funnel_id", invalidMessage),
    sectionId: requiredRpcString(row, "section_id", invalidMessage),
    name: requiredRpcString(row, "name", invalidMessage),
    key,
    type: requiredRpcEnum(row, "field_type", leadFieldTypes, invalidMessage),
    order: requiredRpcSafeInteger(row, "position", 0, invalidMessage),
    width: requiredRpcEnum(row, "width", leadFieldWidths, invalidMessage),
    height: requiredRpcEnum(row, "height", leadFieldHeights, invalidMessage),
    required: requiredRpcBoolean(row, "required", invalidMessage),
    inForm: requiredRpcBoolean(row, "in_form", invalidMessage),
    inMetrics: requiredRpcBoolean(row, "in_metrics", invalidMessage),
    placeholder: optionalRpcText(row, "placeholder", invalidMessage),
    helpText: optionalRpcText(row, "help_text", invalidMessage),
    archived: optionalRpcString(row, "archived_at", invalidMessage) !== undefined,
  };
}

function parseRemoteLeadFieldOptionRow(value: unknown): LeadFieldOption {
  const invalidMessage = "Uma linha remota de opção de campo é inválida.";
  const row = rpcRecord(value, invalidMessage);
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    fieldId: requiredRpcString(row, "field_id", invalidMessage),
    name: requiredRpcString(row, "name", invalidMessage),
    color: optionalRpcText(row, "color", invalidMessage),
    order: requiredRpcSafeInteger(row, "position", 0, invalidMessage),
    archived: optionalRpcString(row, "archived_at", invalidMessage) !== undefined,
  };
}

function indexRemoteRowsById<T extends { id: string }>(rows: readonly T[], invalidMessage: string): Map<string, T> {
  const indexed = new Map<string, T>();
  for (const row of rows) {
    if (indexed.has(row.id)) throw new Error(`${invalidMessage}: id.`);
    indexed.set(row.id, row);
  }
  return indexed;
}

function parseRemoteLeadFieldValue(
  value: unknown,
  fieldType: LeadFieldType,
  invalidMessage: string,
): LeadFieldValue["value"] {
  if (value === null) return null;
  if (fieldType === "boolean") {
    if (typeof value !== "boolean") throw new Error(`${invalidMessage}: value_json.`);
    return value;
  }
  if (fieldType === "number") {
    if (typeof value !== "number" || !Number.isSafeInteger(value))
      throw new Error(`${invalidMessage}: value_json.`);
    return value;
  }
  if (fieldType === "currency") {
    if (typeof value !== "number" || !Number.isFinite(value) || Math.abs(value) > Number.MAX_SAFE_INTEGER)
      throw new Error(`${invalidMessage}: value_json.`);
    return value;
  }
  if (fieldType === "select") {
    if (typeof value !== "string" || !value.trim()) throw new Error(`${invalidMessage}: value_json.`);
    return value.trim();
  }
  if (fieldType === "multi_select") {
    if (!isDenseRpcArray(value)) throw new Error(`${invalidMessage}: value_json.`);
    const normalized = value.map((item) => {
      if (typeof item !== "string" || !item.trim()) throw new Error(`${invalidMessage}: value_json.`);
      return item.trim();
    });
    if (new Set(normalized).size !== normalized.length) throw new Error(`${invalidMessage}: value_json.`);
    return normalized;
  }
  if (typeof value !== "string") throw new Error(`${invalidMessage}: value_json.`);
  return value;
}

function assertRemoteLeadFieldValueOptions(
  fieldType: LeadFieldType,
  value: LeadFieldValue["value"],
  optionIds: ReadonlySet<string>,
  invalidMessage: string,
): void {
  if (fieldType === "select" && value !== null && (typeof value !== "string" || !optionIds.has(value)))
    throw new Error(`${invalidMessage}: value_json.`);
  if (fieldType === "multi_select" && value !== null && (!Array.isArray(value) || value.some((item) => typeof item !== "string" || !optionIds.has(item))))
    throw new Error(`${invalidMessage}: value_json.`);
}

function parseRemoteLeadFieldValueRow(
  value: unknown,
  fieldsById: ReadonlyMap<string, LeadFieldDefinition>,
  optionIdsByField: ReadonlyMap<string, ReadonlySet<string>>,
  leadIds: ReadonlySet<string>,
): LeadFieldValue {
  const invalidMessage = "Uma linha remota de valor de campo é inválida.";
  const row = rpcRecord(value, invalidMessage);
  const leadId = requiredRpcString(row, "lead_id", invalidMessage);
  if (!leadIds.has(leadId)) throw new Error(`${invalidMessage}: lead_id.`);
  const fieldId = requiredRpcString(row, "field_id", invalidMessage);
  const field = fieldsById.get(fieldId);
  if (!field) throw new Error(`${invalidMessage}: field_id.`);
  const parsedValue = parseRemoteLeadFieldValue(rpcField(row, "value_json", invalidMessage), field.type, invalidMessage);
  assertRemoteLeadFieldValueOptions(field.type, parsedValue, optionIdsByField.get(fieldId) ?? new Set<string>(), invalidMessage);
  return { leadId, fieldId, value: parsedValue };
}

function assertRemoteLeadFieldFunnelRelationships(
  leadRows: readonly RemoteLeadRow[],
  funnelRows: readonly Funnel[],
  formSectionRows: readonly LeadFormSection[],
  leadFieldRows: readonly LeadFieldDefinition[],
  leadFieldValueRows: readonly LeadFieldValue[],
): void {
  const funnelIds = new Set(funnelRows.map((funnel) => funnel.id));
  const leadById = new Map(leadRows.map((lead) => [lead.id, lead] as const));
  const fieldById = new Map(leadFieldRows.map((field) => [field.id, field] as const));

  for (const section of formSectionRows) {
    if (!funnelIds.has(section.funnelId))
      throw new Error("Uma linha remota de seção de formulário é inválida: funnel_id.");
  }
  for (const field of leadFieldRows) {
    if (!funnelIds.has(field.funnelId))
      throw new Error("Uma linha remota de definição de campo é inválida: funnel_id.");
  }
  for (const value of leadFieldValueRows) {
    const lead = leadById.get(value.leadId);
    const field = fieldById.get(value.fieldId);
    if (lead?.funnelId !== undefined && field && field.funnelId !== lead.funnelId)
      throw new Error("Uma linha remota de valor de campo é inválida: field_id/funnel_id.");
  }
}

type RemoteLeadCatalogAssociation = { leadId: string; catalogId: string };

function assertRemoteCatalogFunnelRelationships(
  rows: readonly { funnelId: string }[],
  funnelIds: ReadonlySet<string>,
  invalidMessage: string,
): void {
  for (const row of rows) {
    if (!funnelIds.has(row.funnelId)) throw new Error(`${invalidMessage}: funnel_id.`);
  }
}

function parseRemoteLeadTagRow(value: unknown): RemoteLeadCatalogAssociation {
  const invalidMessage = "Uma linha remota de associação de tag é inválida.";
  const row = rpcRecord(value, invalidMessage);
  return {
    leadId: requiredRpcString(row, "lead_id", invalidMessage),
    catalogId: requiredRpcString(row, "tag_id", invalidMessage),
  };
}

function parseRemoteLeadTriggerRow(value: unknown): RemoteLeadCatalogAssociation {
  const invalidMessage = "Uma linha remota de associação de gatilho é inválida.";
  const row = rpcRecord(value, invalidMessage);
  return {
    leadId: requiredRpcString(row, "lead_id", invalidMessage),
    catalogId: requiredRpcString(row, "trigger_id", invalidMessage),
  };
}

function assertRemoteLeadCatalogAssociations(
  rows: readonly RemoteLeadCatalogAssociation[],
  leadRows: ReadonlyMap<string, RemoteLeadRow>,
  catalogRows: ReadonlyMap<string, { funnelId: string }>,
  invalidMessage: string,
): void {
  const pairs = new Set<string>();
  for (const row of rows) {
    const lead = leadRows.get(row.leadId);
    const catalog = catalogRows.get(row.catalogId);
    if (!lead || !catalog) throw new Error(`${invalidMessage}: referência.`);
    if (lead.funnelId !== undefined && catalog.funnelId !== lead.funnelId)
      throw new Error(`${invalidMessage}: funnel_id.`);
    const pair = `${row.leadId}\u0000${row.catalogId}`;
    if (pairs.has(pair)) throw new Error(`${invalidMessage}: duplicidade.`);
    pairs.add(pair);
  }
}

function parseRemoteActivityEventRow(value: unknown): ActivityEvent {
  const invalidMessage = "Uma linha remota de atividade é inválida.";
  const row = rpcRecord(value, invalidMessage);
  const metadataValue = rpcField(row, "metadata", invalidMessage);
  const metadata = metadataValue === undefined
    ? {}
    : rpcRecord(metadataValue, `${invalidMessage}: metadata.`);
  const eventType = requiredRpcString(row, "event_type", invalidMessage);
  const stageId = optionalRpcString(metadata, "stage_id", invalidMessage)
    ?? optionalRpcString(metadata, "to_stage_id", invalidMessage);
  const actorTypeValue = optionalRpcString(row, "actor_type", invalidMessage);
  if (actorTypeValue !== undefined && !commandActorTypes.includes(actorTypeValue as (typeof commandActorTypes)[number]))
    throw new Error(`${invalidMessage}: actor_type.`);
  const originValue = optionalRpcString(row, "origin", invalidMessage);
  if (originValue !== undefined && !commandOrigins.includes(originValue as (typeof commandOrigins)[number]))
    throw new Error(`${invalidMessage}: origin.`);
  const kind: ActivityEvent["kind"] = eventType === "stage_changed"
    ? "stage"
    : eventType === "lead_won" || eventType === "lead_lost" || eventType === "lead_reactivated"
      ? "outcome"
      : "event";
  return {
    id: requiredRpcString(row, "id", invalidMessage),
    leadId: requiredRpcString(row, "lead_id", invalidMessage),
    membershipId: optionalRpcString(row, "membership_id", invalidMessage),
    type: eventType,
    title: requiredRpcString(row, "title", invalidMessage),
    description: optionalRpcString(row, "body", invalidMessage),
    occurredAt: requiredRpcString(row, "occurred_at", invalidMessage),
    stageId,
    fromStageId: optionalRpcString(metadata, "from_stage_id", invalidMessage),
    lossReasonId: optionalRpcString(metadata, "loss_reason_id", invalidMessage),
    kind,
    taskId: optionalRpcString(row, "task_id", invalidMessage),
    actorType: actorTypeValue as ActivityEvent["actorType"],
    actorId: optionalRpcString(row, "actor_id", invalidMessage),
    origin: originValue as ActivityEvent["origin"],
    commandId: optionalRpcString(row, "command_id", invalidMessage),
    correlationId: optionalRpcString(row, "correlation_id", invalidMessage),
    causationId: optionalRpcString(row, "causation_id", invalidMessage),
    countsAsInteraction: optionalRpcBoolean(row, "counts_as_interaction", invalidMessage) ?? false,
  };
}

function assertRemoteCoreRelationships(
  leadRows: readonly RemoteLeadRow[],
  funnelRows: readonly Funnel[],
  stageRows: readonly Stage[],
  lossReasonRows: readonly LossReason[],
  membershipRows: readonly Membership[],
  taskRows: readonly Task[],
  eventRows: readonly ActivityEvent[],
  linkRows: readonly LeadLink[],
  attachmentRows: readonly RemoteAttachmentRow[],
): void {
  const funnelIds = new Set(funnelRows.map((funnel) => funnel.id));
  const stageById = new Map(stageRows.map((stage) => [stage.id, stage]));
  const lossReasonById = new Map(lossReasonRows.map((reason) => [reason.id, reason]));
  const leadById = new Map(leadRows.map((lead) => [lead.id, lead]));
  const membershipById = new Map(membershipRows.map((membership) => [membership.id, membership]));
  const taskById = new Map(taskRows.map((task) => [task.id, task]));

  for (const lead of leadRows) {
    if (lead.funnelId !== undefined && !funnelIds.has(lead.funnelId))
      throw new Error("Uma linha remota de lead é inválida: funnel_id.");
  }
  for (const stage of stageRows) {
    if (!funnelIds.has(stage.funnelId))
      throw new Error("Uma linha remota de etapa é inválida: funnel_id.");
  }
  for (const reason of lossReasonRows) {
    if (!funnelIds.has(reason.funnelId))
      throw new Error("Uma linha remota de motivo de perda é inválida: funnel_id.");
  }
  for (const membership of membershipRows) {
    const lead = leadById.get(membership.leadId);
    const stage = stageById.get(membership.stageId);
    if (!lead || !funnelIds.has(membership.funnelId) || !stage || stage.funnelId !== membership.funnelId)
      throw new Error("Uma linha remota de participação é inválida: referência.");
    if (lead.funnelId !== undefined && lead.funnelId !== membership.funnelId)
      throw new Error("Uma linha remota de participação é inválida: funnel_id.");
    if (membership.lossReasonId !== undefined) {
      const reason = lossReasonById.get(membership.lossReasonId);
      if (!reason || reason.funnelId !== membership.funnelId)
        throw new Error("Uma linha remota de participação é inválida: loss_reason_id.");
    }
  }
  for (const task of taskRows) {
    const lead = task.leadId ? leadById.get(task.leadId) : undefined;
    const membership = task.membershipId === undefined ? undefined : membershipById.get(task.membershipId);
    if (!lead || (task.membershipId !== undefined && (!membership || membership.leadId !== task.leadId)))
      throw new Error("Uma linha remota de tarefa é inválida: referência.");
  }
  for (const event of eventRows) {
    const lead = leadById.get(event.leadId);
    const membership = event.membershipId === undefined ? undefined : membershipById.get(event.membershipId);
    const task = event.taskId === undefined ? undefined : taskById.get(event.taskId);
    const stage = event.stageId === undefined ? undefined : stageById.get(event.stageId);
    if (!lead || (event.membershipId !== undefined && (!membership || membership.leadId !== event.leadId)))
      throw new Error("Uma linha remota de atividade é inválida: referência.");
    if (event.taskId !== undefined && (!task || task.leadId !== event.leadId))
      throw new Error("Uma linha remota de atividade é inválida: task_id.");
    if (event.membershipId !== undefined && task && task.membershipId !== event.membershipId)
      throw new Error("Uma linha remota de atividade é inválida: membership_id.");
    if (event.stageId !== undefined && (!stage || (membership && stage.funnelId !== membership.funnelId)))
      throw new Error("Uma linha remota de atividade é inválida: stage_id.");
    if (lead.funnelId !== undefined) {
      if (membership && membership.funnelId !== lead.funnelId)
        throw new Error("Uma linha remota de atividade é inválida: membership_id/funnel_id.");
      if (stage && stage.funnelId !== lead.funnelId)
        throw new Error("Uma linha remota de atividade é inválida: stage_id/funnel_id.");
    }
  }
  for (const link of linkRows) {
    if (!leadById.has(link.leadId)) throw new Error("Uma linha remota de link é inválida: lead_id.");
  }
  for (const attachment of attachmentRows) {
    if (!leadById.has(attachment.leadId)) throw new Error("Uma linha remota de anexo é inválida: lead_id.");
  }
}

function assertRemoteIdentityRelationships(
  leadRows: readonly RemoteLeadRow[],
  funnelRows: readonly Funnel[],
  companies: readonly Company[],
  contacts: readonly Contact[],
  leadContacts: readonly LeadContact[],
  leadRelations: readonly LeadRelation[],
): void {
  const companyById = new Map(companies.map((company) => [company.id, company] as const));
  const contactById = new Map(contacts.map((contact) => [contact.id, contact] as const));
  const leadById = new Map(leadRows.map((lead) => [lead.id, lead] as const));
  const funnelIds = new Set(funnelRows.map((funnel) => funnel.id));
  const leadContactIdsByLead = new Map<string, LeadContact[]>();

  for (const lead of leadRows) {
    const company = lead.companyId === undefined ? undefined : companyById.get(lead.companyId);
    if (lead.companyId !== undefined && !company)
      throw new Error("Uma linha remota de lead é inválida: company_id.");
    if (lead.primaryContactId !== undefined) {
      const primaryContact = contactById.get(lead.primaryContactId);
      if (!primaryContact || (company && primaryContact.companyId !== company.id))
        throw new Error("Uma linha remota de lead é inválida: primary_contact_id.");
    }
  }

  for (const link of leadContacts) {
    const lead = leadById.get(link.leadId);
    const contact = contactById.get(link.contactId);
    if (!lead || !contact)
      throw new Error("Uma linha remota de vínculo de contato é inválida: referência.");
    if (lead.companyId !== undefined && contact.companyId !== lead.companyId)
      throw new Error("Uma linha remota de vínculo de contato é inválida: company_id.");
    const links = leadContactIdsByLead.get(link.leadId) ?? [];
    links.push(link);
    leadContactIdsByLead.set(link.leadId, links);
  }

  for (const lead of leadRows) {
    const links = leadContactIdsByLead.get(lead.id) ?? [];
    const primaryLinks = links.filter((link) => link.primary);
    if (links.length > 0 && primaryLinks.length !== 1)
      throw new Error("Uma linha remota de vínculo de contato é inválida: is_primary.");
    if (lead.primaryContactId !== undefined) {
      if (!links.some((link) => link.contactId === lead.primaryContactId))
        throw new Error("Uma linha remota de lead é inválida: primary_contact_id.");
      if (primaryLinks.length !== 1 || primaryLinks[0].contactId !== lead.primaryContactId)
        throw new Error("Uma linha remota de vínculo de contato é inválida: is_primary.");
    } else if (links.length > 0) {
      throw new Error("Uma linha remota de vínculo de contato é inválida: primary_contact_id.");
    }
  }

  for (const relation of leadRelations) {
    const source = leadById.get(relation.sourceLeadId);
    const target = leadById.get(relation.targetLeadId);
    if (!source || !target)
      throw new Error("Uma linha remota de relação de lead é inválida: referência.");
    if (relation.sourceFunnelId !== undefined) {
      if (!funnelIds.has(relation.sourceFunnelId) || source.funnelId !== relation.sourceFunnelId)
        throw new Error("Uma linha remota de relação de lead é inválida: source_funnel_id.");
    }
    if (relation.targetFunnelId !== undefined) {
      if (!funnelIds.has(relation.targetFunnelId) || target.funnelId !== relation.targetFunnelId)
        throw new Error("Uma linha remota de relação de lead é inválida: target_funnel_id.");
    }
    if (relation.relationType === "cross_funnel" && (
      relation.sourceFunnelId === undefined
      || relation.targetFunnelId === undefined
      || relation.sourceFunnelId === relation.targetFunnelId
    )) {
      throw new Error("Uma linha remota de relação de lead é inválida: funnel_id.");
    }
  }
}

async function taskCommandRpcsEnabled(): Promise<boolean> {
  const capabilities = await loadIdentityCapabilities(client());
  return capabilities.status === "available" && capabilities.taskCommands;
}

async function loadLegacyTask(taskId: string, ownerIdValue: string): Promise<Task> {
  const result = await client()
    .from("tasks")
    .select("*")
    .eq("id", taskId)
    .eq("owner_id", ownerIdValue)
    .maybeSingle();
  if (result.error) throw result.error;
  if (!result.data) throw new Error("Tarefa não encontrada ou sem permissão.");
  return parseRemoteTaskRow(result.data);
}

function legacyTaskResult(
  context: CommandContext,
  task: Task,
  transition: TaskCommandValue["transition"],
  occurredAt: string,
): CommandResult<TaskCommandValue> {
  // Tarefa interna não tem jornada onde o evento apareceria (§7.4).
  const event: ActivityEvent | undefined = task.leadId
    ? {
    id: crypto.randomUUID(),
    leadId: task.leadId,
    membershipId: task.membershipId,
    taskId: task.id,
    type: `task_${transition}`,
    title: transition === "created" ? task.description : transition === "completed" ? "Tarefa concluída" : "Tarefa reaberta",
    description: task.description,
    occurredAt,
    kind: "event",
    actorType: context.actorType,
    actorId: context.actorId,
    origin: context.origin,
    commandId: context.commandId,
    correlationId: context.correlationId,
    causationId: context.causationId,
    countsAsInteraction: false,
      }
    : undefined;
  return {
    ok: true,
    status: "applied",
    value: { task, event, transition },
    commandId: context.commandId,
    correlationId: context.correlationId,
    version: task.version,
    eventIds: event ? [event.id] : [],
    outboxJobIds: [],
    auditId: crypto.randomUUID(),
    warnings: ["task_commands=false: operação executada pelo contrato legado; auditoria tipada será ativada somente após staging."],
  };
}

async function repositoryTaskContext(
  command: string,
  context?: CommandContext,
): Promise<CommandContext> {
  if (context && context.actorId !== "authenticated-user") return context;
  const actorId = await ownerId();
  if (context) return { ...context, actorId };
  return createCommandContext({
    ownerId: actorId,
    actorType: "human",
    actorId,
    origin: "api",
    idempotencyKey: `${command}:${crypto.randomUUID()}`,
  });
}
export async function ownerId() {
  const { data, error } = await client().auth.getUser();
  if (error || !data.user) throw error ?? new Error("Sessão ausente");
  return data.user.id;
}

export async function loadRemoteData(): Promise<AppData> {
  const c = client();
  const currentOwnerId = await ownerId();
  const [
    leads,
    funnels,
    stages,
    memberships,
    tasks,
    events,
    tags,
    reasons,
    views,
    attachments,
    links,
    leadTags,
    triggers,
    leadTriggers,
  ] = await Promise.all([
      c.from("leads").select("*").order("created_at", { ascending: false }),
      c.from("funnels").select("*").order("position"),
      c.from("stages").select("*").order("position"),
      c.from("pipeline_memberships").select("*"),
      c.from("tasks").select("*").order("due_at"),
      c.from("activity_events").select("*").order("occurred_at"),
      c.from("tags").select("*").order("position"),
      c.from("loss_reasons").select("*").order("position"),
      c.from("saved_views").select("*").order("position"),
      c
        .from("attachments")
        .select("*")
        .order("created_at", { ascending: false }),
      c.from("links").select("*").order("created_at", { ascending: false }),
      c.from("lead_tags").select("*"),
      c.from("triggers").select("*").order("position"),
    c.from("lead_triggers").select("*"),
  ]);
  // The form migration is intentionally deployable after the application
  // code. Missing optional tables must not blank the entire remote workspace;
  // legacy leads remain readable until the migration is applied.
  const [optionalResults, declaredCapabilities] = await Promise.all([
    Promise.all([
    c.from("lead_form_sections").select("*").order("position"),
    c.from("lead_field_definitions").select("*").order("position"),
    c.from("lead_field_options").select("*").order("position"),
    c.from("lead_field_values").select("*"),
    c.from("classification_definitions").select("*").order("position"),
    c.from("classification_options").select("*").order("position"),
    c.from("lead_classification_values").select("*"),
    c.from("notification_states").select("*"),
    c.from("user_preferences").select("*"),
    c.from("meetings").select("*").order("starts_at"),
    ]),
    loadIdentityCapabilities(c),
  ]);
  const [
    formSections,
    leadFields,
    leadFieldOptions,
    leadFieldValues,
    classificationDefinitions,
    classificationOptions,
    leadClassificationValues,
    notificationStates,
    userPreferences,
    meetings,
  ] = optionalResults.map((result) => {
    if (result.error) {
      console.warn("Formulários opcionais ainda não estão disponíveis no Supabase; usando o contrato legado quando possível.", result.error.message);
      return { data: [], error: null };
    }
    return result;
  }) as typeof optionalResults;
  const identityResult = await loadIdentityRows(c, declaredCapabilities.identityFoundation, currentOwnerId);
  const identityCapabilities = identityResult.error
    ? {
        ...declaredCapabilities,
        status: "degraded" as const,
        identityFoundation: false,
        canonicalLeadCreation: false,
        message: identityResult.error.message ?? "Falha ao ler a fundação de identidade.",
      }
    : declaredCapabilities;
  const [companies, contacts, partyChannels, leadContacts, leadRelations, identityMatchCandidates] = identityResult.rows;
  const error = [
    leads,
    funnels,
    stages,
    memberships,
    tasks,
    events,
    tags,
    reasons,
    views,
    attachments,
    links,
    leadTags,
    triggers,
    leadTriggers,
  ].find((x) => x.error)?.error;
  if (error) throw error;
  if (!(funnels.data ?? []).length) {
    const { error: bootstrapError } = await c.rpc("bootstrap_workspace");
    if (bootstrapError) throw bootstrapError;
    return loadRemoteData();
  }
  const funnelRows = remoteRows(funnels.data, "funnels").map(parseRemoteFunnelRow);
  const stageRows = remoteRows(stages.data, "stages").map(parseRemoteStageRow);
  const membershipRows = remoteRows(memberships.data, "pipeline_memberships").map(parseRemoteMembershipRow);
  const taskRows = remoteRows(tasks.data, "tasks").map(parseRemoteTaskRow);
  const eventRows = remoteRows(events.data, "activity_events").map(parseRemoteActivityEventRow);
  const attachmentRows = remoteRows(attachments.data, "attachments").map(parseRemoteAttachmentRow);
  const linkRows = remoteRows(links.data, "links").map(parseRemoteLinkRow);
  const defaultFunnelId =
    funnelRows.find((funnel) => !funnel.archived)?.id ??
    funnelRows[0]?.id ??
    "";
  const leadRows = remoteRows(leads.data, "leads").map(parseRemoteLeadRow);
  const leadRowsById = new Map(leadRows.map((lead) => [lead.id, lead] as const));
  const leadIds = new Set(leadRowsById.keys());
  const lossReasonRows = remoteRows(reasons.data, "loss_reasons")
    .map((row, index) => parseRemoteLossReasonRow(row, index, defaultFunnelId));
  const funnelIds = new Set(funnelRows.map((funnel) => funnel.id));
  const tagRows = remoteRows(tags.data, "tags")
    .map((row, index) => parseRemoteTagRow(row, index, defaultFunnelId))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const triggerRows = remoteRows(triggers.data, "triggers")
    .map((row, index) => parseRemoteTriggerRow(row, index, defaultFunnelId))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const leadTagRows = remoteRows(leadTags.data, "lead_tags").map(parseRemoteLeadTagRow);
  const leadTriggerRows = remoteRows(leadTriggers.data, "lead_triggers").map(parseRemoteLeadTriggerRow);
  assertRemoteLeadCatalogAssociations(
    leadTagRows,
    leadRowsById,
    new Map(tagRows.map((tag) => [tag.id, tag] as const)),
    "Uma linha remota de associação de tag é inválida.",
  );
  assertRemoteLeadCatalogAssociations(
    leadTriggerRows,
    leadRowsById,
    new Map(triggerRows.map((trigger) => [trigger.id, trigger] as const)),
    "Uma linha remota de associação de gatilho é inválida.",
  );
  assertRemoteCatalogFunnelRelationships(tagRows, funnelIds, "Uma linha remota de tag é inválida.");
  assertRemoteCatalogFunnelRelationships(triggerRows, funnelIds, "Uma linha remota de gatilho é inválida.");
  assertRemoteCatalogFunnelRelationships(lossReasonRows, funnelIds, "Uma linha remota de motivo de perda é inválida.");
  const savedViewRows = remoteRows(views.data, "saved_views").map(parseRemoteSavedViewRow);
  indexRemoteRowsById(savedViewRows, "Uma linha remota de visualização é inválida.");
  assertRemoteSavedViewRelationships(
    savedViewRows,
    funnelRows,
    stageRows,
    lossReasonRows,
    tagRows,
    triggerRows,
  );
  const formSectionRows = remoteRows(formSections.data, "lead_form_sections").map(parseRemoteFormSectionRow);
  const leadFieldRows = remoteRows(leadFields.data, "lead_field_definitions").map(parseRemoteLeadFieldRow);
  const leadFieldOptionRows = remoteRows(leadFieldOptions.data, "lead_field_options").map(parseRemoteLeadFieldOptionRow);
  const formSectionsById = indexRemoteRowsById(formSectionRows, "Uma linha remota de seção de formulário é inválida.");
  const leadFieldsById = indexRemoteRowsById(leadFieldRows, "Uma linha remota de definição de campo é inválida.");
  indexRemoteRowsById(leadFieldOptionRows, "Uma linha remota de opção de campo é inválida.");
  for (const field of leadFieldRows) {
    const section = formSectionsById.get(field.sectionId);
    if (!section || section.funnelId !== field.funnelId)
      throw new Error("Uma linha remota de definição de campo é inválida: section_id.");
  }
  const optionIdsByField = new Map<string, Set<string>>();
  for (const option of leadFieldOptionRows) {
    if (!leadFieldsById.has(option.fieldId)) throw new Error("Uma linha remota de opção de campo é inválida: field_id.");
    const optionIds = optionIdsByField.get(option.fieldId) ?? new Set<string>();
    if (optionIds.has(option.id)) throw new Error("Uma linha remota de opção de campo é inválida: id.");
    optionIds.add(option.id);
    optionIdsByField.set(option.fieldId, optionIds);
  }
  const leadFieldValueRows = remoteRows(leadFieldValues.data, "lead_field_values").map((row) => parseRemoteLeadFieldValueRow(
    row,
    leadFieldsById,
    optionIdsByField,
    leadIds,
  ));
  const valueKeys = new Set<string>();
  for (const value of leadFieldValueRows) {
    const key = `${value.leadId}\u0000${value.fieldId}`;
    if (valueKeys.has(key)) throw new Error("Uma linha remota de valor de campo é inválida: lead_id/field_id.");
    valueKeys.add(key);
  }
  assertRemoteLeadFieldFunnelRelationships(
    leadRows,
    funnelRows,
    formSectionRows,
    leadFieldRows,
    leadFieldValueRows,
  );
  assertRemoteCoreRelationships(
    leadRows,
    funnelRows,
    stageRows,
    lossReasonRows,
    membershipRows,
    taskRows,
    eventRows,
    linkRows,
    attachmentRows,
  );
  const companyRows = companies.map(mapCompanyRow);
  const contactRows = contacts.map(mapContactRow);
  const leadContactDomainRows = leadContacts.map(mapLeadContactRow);
  const leadRelationDomainRows = leadRelations.map(mapLeadRelationRow);
  assertRemoteIdentityRelationships(
    leadRows,
    funnelRows,
    companyRows,
    contactRows,
    leadContactDomainRows,
    leadRelationDomainRows,
  );
  for (const lead of leadRows) {
    if (lead.photoPath)
      assertOwnedStoragePath(lead.photoPath, currentOwnerId, lead.id, "Uma linha remota de foto de lead é inválida.");
  }
  for (const attachment of attachmentRows) {
    if (attachment.ownerId !== currentOwnerId)
      throw new Error("Uma linha remota de anexo é inválida: owner_id.");
    assertOwnedStoragePath(
      attachment.storagePath,
      currentOwnerId,
      attachment.leadId,
      "Uma linha remota de anexo é inválida.",
    );
  }
  const photoUrls = new Map<string, string>(),
    attachmentUrls = new Map<string, string>();
  await Promise.all(
    leadRows
      .filter((lead) => lead.photoPath)
      .map(async (lead) => {
        const { data } = await c.storage
          .from(leadFilesBucket)
          .createSignedUrl(lead.photoPath!, 3600);
        if (data?.signedUrl) photoUrls.set(lead.id, data.signedUrl);
      }),
  );
  await Promise.all(
    attachmentRows.map(async (attachment) => {
      const { data } = await c.storage
        .from(attachment.bucket)
        .createSignedUrl(attachment.storagePath, 3600);
      if (data?.signedUrl) attachmentUrls.set(attachment.id, data.signedUrl);
    }),
  );
  return {
    ...empty,
    identityCapabilityStatus: identityCapabilities.status,
    identityCapabilitySchemaVersion: identityCapabilities.schemaVersion,
    identityCapabilityMessage: identityCapabilities.message,
    identityFoundationAvailable: identityCapabilities.identityFoundation,
    canonicalLeadCreationAvailable: identityCapabilities.canonicalLeadCreation,
    canonicalLeadCreationCanaryReady: identityCapabilities.canonicalLeadCreationCanaryReady,
    canonicalLeadCreationCanary: identityCapabilities.canonicalLeadCreationCanary,
    // A Inbox remota so liga quando o contrato server-side declarar. Sem
    // capability positiva o frontend nao le nem escreve o canal de e-mail.
    emailInboxAvailable: identityCapabilities.emailInbox,
    emailSendAvailable: identityCapabilities.emailSend,
    // A assistencia de IA remota depende do mesmo contrato server-side. Sem
    // capability positiva nao ha contexto, chamada nem resultado.
    aiAssistanceAvailable: identityCapabilities.aiAssistance,
    aiAutoSendAvailable: identityCapabilities.aiAutoSend,
    // Definição, publicação e execução de flows dependem do mesmo contrato
    // server-side. Sem capability positiva, nada de flow é lido ou escrito.
    flowDefinitionsAvailable: identityCapabilities.flowDefinitions,
    flowPublicationAvailable: identityCapabilities.flowPublication,
    flowExecutionAvailable: identityCapabilities.flowExecution,
    financeAvailable: identityCapabilities.finance,
    classificationDefinitions: remoteRows(classificationDefinitions.data, "classification_definitions")
      .map(parseRemoteClassificationDefinitionRow)
      .sort((left, right) => left.order - right.order),
    classificationOptions: remoteRows(classificationOptions.data, "classification_options")
      .map(parseRemoteClassificationOptionRow)
      .sort((left, right) => left.order - right.order),
    leadClassificationValues: remoteRows(leadClassificationValues.data, "lead_classification_values")
      .map(parseRemoteLeadClassificationValueRow),
    notificationStates: remoteRows(notificationStates.data, "notification_states")
      .map(parseRemoteNotificationStateRow),
    userPreferences: remoteRows(userPreferences.data, "user_preferences")
      .map(parseRemoteUserPreferenceRow),
    meetings: remoteRows(meetings.data, "meetings")
      .map(parseRemoteMeetingRow)
      .sort((left, right) => left.startsAt.localeCompare(right.startsAt)),
    leads: leadRows.map((lead) => ({
      id: lead.id,
      companyId: lead.companyId,
      primaryContactId: lead.primaryContactId,
      contactIds: leadContacts.filter((item) => item.lead_id === lead.id).map((item) => item.contact_id),
      name: lead.name,
      company: lead.company,
      role: lead.role,
      potentialValue: lead.potentialValue,
      photoUrl: photoUrls.get(lead.id),
      email: lead.email,
      phone: lead.phone,
      site: lead.site,
      instagram: lead.instagram,
      biggestPain: lead.biggestPain,
      biggestObjection: lead.biggestObjection,
      bestOverdelivery: lead.bestOverdelivery,
      triggerIds: leadTriggerRows
        .filter((trigger) => trigger.leadId === lead.id)
        .map((trigger) => trigger.catalogId),
      city: lead.city,
      state: lead.state,
      segment: lead.segment,
      source: lead.source,
      priority: lead.priority,
      tagIds: leadTagRows
        .filter((tag) => tag.leadId === lead.id)
        .map((tag) => tag.catalogId),
      createdAt: lead.createdAt,
      lastInteractionAt: lead.lastInteractionAt,
      archived: lead.archivedAt !== undefined,
      archivedAt: lead.archivedAt,
      mergedIntoId: lead.mergedIntoId,
    })),
    companies: companyRows,
    contacts: contactRows,
    partyChannels: partyChannels.map(mapPartyChannelRow),
    leadContacts: leadContactDomainRows,
    leadRelations: leadRelationDomainRows,
    identityMatchCandidates: identityMatchCandidates.map(mapIdentityCandidateRow),
    // Coluna candidata ainda não aplicada: funil remoto legado é lido como
    // `simple` sem reinterpretar etapas, visitas, ordenação ou analytics.
    funnels: funnelRows,
    stages: stageRows,
    memberships: membershipRows,
    tasks: taskRows,
    events: eventRows,
    tags: tagRows,
    triggers: triggerRows,
    lossReasons: lossReasonRows,
    savedViews: savedViewRows,
    attachments: attachmentRows.map((attachment) => ({
      id: attachment.id,
      leadId: attachment.leadId,
      name: attachment.name,
      type: attachment.type,
      size: attachment.size,
      dataUrl: attachmentUrls.get(attachment.id),
      createdAt: attachment.createdAt,
    })),
    links: linkRows,
    formSections: formSectionRows,
    leadFields: leadFieldRows,
    leadFieldOptions: leadFieldOptionRows,
    leadFieldValues: leadFieldValueRows,
  };
}

async function assertCanonicalLeadCreationCapability() {
  const capabilities = await loadIdentityCapabilities(client());
  if (!capabilities.identityFoundation)
    throw new Error(capabilities.message ?? "A fundação de identidade não está disponível neste ambiente.");
  if (!capabilities.canonicalLeadCreation && !capabilities.canonicalLeadCreationCanary)
    throw new Error("canonical_lead_creation=false e o canário de criação ainda não está habilitado.");
}

async function assertIdentityCandidateCommandCapability(
  unavailableMessage = "Os comandos candidatos de identidade ainda não estão disponíveis neste ambiente.",
) {
  const capabilities = await loadIdentityCapabilities(client());
  if (!capabilities.identityFoundation)
    throw new Error(capabilities.message ?? "A fundação de identidade não está disponível neste ambiente.");
  if (!capabilities.canonicalLeadCreationCanaryReady)
    throw new Error(unavailableMessage);
}

export const remote = {
  async createOrReuseCompanyLead(input: CreateOrReuseCompanyLeadInput) {
    await assertCanonicalLeadCreationCapability();
    return identityRemote.createOrReuseCompanyLead(client(), input);
  },
  async previewRelatedLeadInFunnel(sourceLeadId: string, targetFunnelId: string) {
    await assertCanonicalLeadCreationCapability();
    return identityRemote.previewRelatedLeadInFunnel(client(), sourceLeadId, targetFunnelId);
  },
  async createRelatedLeadInFunnel(input: CreateRelatedLeadInput) {
    await assertCanonicalLeadCreationCapability();
    return identityRemote.createRelatedLeadInFunnel(client(), input);
  },
  async previewReclassifyPartyChannel(channelId: string, targetContactId?: string | null) {
    await assertIdentityCandidateCommandCapability();
    return identityRemote.previewReclassifyPartyChannel(client(), channelId, targetContactId);
  },
  async reclassifyPartyChannel(input: ReclassifyPartyChannelInput) {
    await assertIdentityCandidateCommandCapability();
    return identityRemote.reclassifyPartyChannel(client(), input);
  },
  async reviewIdentityCandidate(input: ReviewIdentityCandidateInput) {
    await assertIdentityCandidateCommandCapability("A fila de candidatos de identidade ainda não está disponível neste ambiente.");
    return identityRemote.reviewIdentityCandidate(client(), input);
  },
  async previewMergeCompanies(sourceCompanyId: string, targetCompanyId: string) {
    await assertIdentityCandidateCommandCapability("A mesclagem de empresas ainda não está disponível neste ambiente.");
    return identityRemote.previewMergeCompanies(client(), sourceCompanyId, targetCompanyId);
  },
  async mergeCompanies(input: MergeCompaniesInput) {
    await assertIdentityCandidateCommandCapability("A mesclagem de empresas ainda não está disponível neste ambiente.");
    return identityRemote.mergeCompanies(client(), input);
  },
  async addCompany(company: Company) {
    await identityRemote.addCompany(client(), await ownerId(), company);
  },
  async updateCompany(id: string, patch: Partial<Company>) {
    await identityRemote.updateCompany(client(), id, patch);
  },
  async addContact(contact: Contact) {
    await identityRemote.addContact(client(), await ownerId(), contact);
  },
  async updateContact(id: string, patch: Partial<Contact>) {
    await identityRemote.updateContact(client(), id, patch);
  },
  async setLeadContacts(
    leadId: string,
    contactIds: string[],
    primaryContactId?: string | null,
  ) {
    await identityRemote.setLeadContacts(client(), leadId, contactIds, primaryContactId);
  },
  async addLead(lead: unknown, funnelId: unknown, stageId: unknown) {
    const parsed = parseRemoteLeadCreateInput(lead, funnelId, stageId);
    const invalidMessage = "O lead para persistência é inválido.";
    const c = client();
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    await requireOwnedRemoteRow(c, "funnels", owner_id, parsed.funnelId, "funnel_id", "owner_id", invalidMessage);
    const stage = await requireOwnedRemoteRow(
      c,
      "stages",
      owner_id,
      parsed.stageId,
      "stage_id",
      "owner_id,funnel_id",
      invalidMessage,
    );
    const stageFunnelId = requiredInputIdentifier(
      requiredRpcString(stage.row, "funnel_id", invalidMessage),
      "funnel_id",
      invalidMessage,
    );
    if (stageFunnelId !== parsed.funnelId) throw new Error(`${invalidMessage}: stage_id/funnel_id.`);
    const tagIds = await requireOwnedCatalogReferences(
      c,
      "tags",
      owner_id,
      parsed.tagIds,
      "tag_id",
      parsed.funnelId,
      invalidMessage,
    );
    const triggerIds = await requireOwnedCatalogReferences(
      c,
      "triggers",
      owner_id,
      parsed.triggerIds,
      "trigger_id",
      parsed.funnelId,
      invalidMessage,
    );
    const insert = await c
        .from("leads")
        .insert({ ...parsed.row, owner_id });
    if (insert.error) throw insert.error;
    if (tagIds.length) {
      const tags = await c
        .from("lead_tags")
        .insert(
          tagIds.map((tagId) => ({
            owner_id,
            lead_id: parsed.leadId,
            tag_id: tagId,
          })),
        );
      if (tags.error) throw tags.error;
    }
    if (triggerIds.length) {
      const triggers = await c
        .from("lead_triggers")
        .insert(
          triggerIds.map((triggerId) => ({
            owner_id,
            lead_id: parsed.leadId,
            trigger_id: triggerId,
          })),
        );
      if (triggers.error) throw triggers.error;
    }
    const membership = await c.rpc("create_membership", {
      p_lead_id: parsed.leadId,
      p_funnel_id: parsed.funnelId,
      p_stage_id: parsed.stageId,
    });
    if (membership.error) throw membership.error;
    return requiredInputIdentifier(
      membership.data,
      "membership_id",
      "A RPC de criação de participação devolveu um resultado inválido.",
    );
  },
  async moveMembership(id: string, stageId: string) {
    const invalidMessage = "A movimentação da participação é inválida.";
    const result = await client().rpc("move_membership", {
      p_membership_id: requiredInputIdentifier(id, "membership_id", invalidMessage),
      p_to_stage_id: requiredInputIdentifier(stageId, "stage_id", invalidMessage),
    });
    if (result.error) throw result.error;
  },
  async deleteJourneyEvent(id: string) {
    const invalidMessage = "O evento de jornada para exclusão é inválido.";
    const result = await client().rpc("delete_journey_event", {
      p_event_id: requiredInputIdentifier(id, "event_id", invalidMessage),
    });
    if (result.error) throw result.error;
  },
  async saveMembershipOrder(ids: string[]) {
    const invalidMessage = "A ordem de participações é inválida.";
    const result = await client().rpc("save_pipeline_order", {
      p_membership_ids: requiredInputIdentifierArray(ids, "membership_ids", invalidMessage),
    });
    if (result.error) throw result.error;
  },
  async saveConfigOrder(
    kind: "funnel" | "stage" | "tag" | "trigger" | "lossReason",
    ids: string[],
    parentId?: string,
  ) {
    const invalidMessage = "A ordem da configuração é inválida.";
    const rpcByKind = {
      funnel: "save_funnel_order",
      stage: "save_stage_order",
      tag: "save_tag_order",
      trigger: "save_trigger_order",
      lossReason: "save_loss_reason_order",
    } as const;
    if (!Object.prototype.hasOwnProperty.call(rpcByKind, kind)) throw new Error(`${invalidMessage}: kind.`);
    const safeIds = requiredInputIdentifierArray(ids, "ids", invalidMessage);
    if (kind !== "stage" && parentId !== undefined) throw new Error(`${invalidMessage}: parentId.`);
    const args =
      kind === "stage"
        ? { p_funnel_id: requiredInputIdentifier(parentId, "funnel_id", invalidMessage), p_stage_ids: safeIds }
        : kind === "funnel"
          ? { p_funnel_ids: safeIds }
          : kind === "tag"
            ? { p_tag_ids: safeIds }
            : kind === "trigger"
              ? { p_trigger_ids: safeIds }
              : { p_loss_reason_ids: safeIds };
    const result = await client().rpc(rpcByKind[kind], args);
    if (result.error) throw result.error;
  },
  async saveFormSectionOrder(funnelId: string, ids: string[]) {
    const invalidMessage = "A ordem das seções de formulário é inválida.";
    const result = await client().rpc("save_lead_form_section_order", {
      p_funnel_id: requiredInputIdentifier(funnelId, "funnel_id", invalidMessage),
      p_section_ids: requiredInputIdentifierArray(ids, "section_ids", invalidMessage),
    });
    if (result.error) throw result.error;
  },
  async saveLeadFieldOrder(sectionId: string, ids: string[]) {
    const invalidMessage = "A ordem dos campos de formulário é inválida.";
    const result = await client().rpc("save_lead_field_order", {
      p_section_id: requiredInputIdentifier(sectionId, "section_id", invalidMessage),
      p_field_ids: requiredInputIdentifierArray(ids, "field_ids", invalidMessage),
    });
    if (result.error) throw result.error;
  },
  async closeMembership(id: string, status: "won" | "lost", reason?: string) {
    const invalidMessage = "O encerramento da participação é inválido.";
    const safeId = requiredInputIdentifier(id, "membership_id", invalidMessage);
    if (status !== "won" && status !== "lost") throw new Error(`${invalidMessage}: status.`);
    const safeReason = reason === undefined || reason === null
      ? null
      : requiredInputIdentifier(reason, "loss_reason_id", invalidMessage);
    if (status === "lost" && safeReason === null) throw new Error(`${invalidMessage}: loss_reason_id.`);
    const result = await client().rpc("close_membership", {
      p_membership_id: safeId,
      p_status: status,
      p_loss_reason_id: safeReason,
    });
    if (result.error) throw result.error;
  },
  async reactivate(id: string) {
    const invalidMessage = "A reativação da participação é inválida.";
    const result = await client().rpc("reactivate_membership", {
      p_membership_id: requiredInputIdentifier(id, "membership_id", invalidMessage),
    });
    if (result.error) throw result.error;
  },
  async createMembership(leadId: string, funnelId: string, stageId: string) {
    const invalidMessage = "A criação da participação é inválida.";
    const result = await client().rpc("create_membership", {
      p_lead_id: requiredInputIdentifier(leadId, "lead_id", invalidMessage),
      p_funnel_id: requiredInputIdentifier(funnelId, "funnel_id", invalidMessage),
      p_stage_id: requiredInputIdentifier(stageId, "stage_id", invalidMessage),
    });
    if (result.error) throw result.error;
    return requiredInputIdentifier(
      result.data,
      "membership_id",
      "A RPC de criação de participação devolveu um resultado inválido.",
    );
  },
  async completeTask(id: string, context?: CommandContext): Promise<CommandResult<TaskCommandValue>> {
    const invalidMessage = "A conclusão da tarefa é inválida.";
    const safeTaskId = requiredInputIdentifier(id, "task_id", invalidMessage);
    const taskContext = await repositoryTaskContext("tasks.completed", context);
    if (!(await taskCommandRpcsEnabled())) {
      const currentOwnerId = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
      const current = await loadLegacyTask(safeTaskId, currentOwnerId);
      if (current.status !== "pending")
        return commandError(taskContext, "invalid_state", "Somente tarefas pendentes podem ser concluídas.", { severity: "warning" });
      if (taskContext.expectedVersion !== undefined && taskContext.expectedVersion !== current.version)
        return commandError(taskContext, "version_conflict", "A tarefa foi alterada por outra operação.", { severity: "warning" });
      const result = await client().rpc("complete_task", { p_task_id: safeTaskId });
      if (result.error) throw result.error;
      const now = new Date().toISOString();
      return legacyTaskResult(taskContext, { ...current, status: "done", completedAt: now, updatedAt: now }, "completed", now);
    }
    const result = await client().rpc("complete_task", {
      p_task_id: safeTaskId,
      p_command_id: taskContext.commandId,
      p_correlation_id: taskContext.correlationId,
      p_causation_id: taskContext.causationId ?? null,
      p_actor_type: taskContext.actorType,
      p_actor_id: taskContext.actorId,
      p_origin: taskContext.origin,
      p_idempotency_key: taskContext.idempotencyKey,
      p_requested_at: taskContext.requestedAt,
      p_expected_version: taskContext.expectedVersion ?? null,
    });
    if (result.error) throw result.error;
    return parseTaskCommandResult(result.data);
  },
  async reopenTask(id: string, context?: CommandContext): Promise<CommandResult<TaskCommandValue>> {
    const invalidMessage = "A reabertura da tarefa é inválida.";
    const safeTaskId = requiredInputIdentifier(id, "task_id", invalidMessage);
    const taskContext = await repositoryTaskContext("tasks.reopened", context);
    if (!(await taskCommandRpcsEnabled())) {
      const currentOwnerId = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
      const current = await loadLegacyTask(safeTaskId, currentOwnerId);
      if (current.status !== "done")
        return commandError(taskContext, "invalid_state", "Somente tarefas concluídas podem ser reabertas.", { severity: "warning" });
      if (taskContext.expectedVersion !== undefined && taskContext.expectedVersion !== current.version)
        return commandError(taskContext, "version_conflict", "A tarefa foi alterada por outra operação.", { severity: "warning" });
      const updated = await client()
        .from("tasks")
        .update({ status: "pending", completed_at: null })
        .eq("id", safeTaskId)
        .eq("owner_id", currentOwnerId)
        .select("id")
        .maybeSingle();
      if (updated.error) throw updated.error;
      if (!updated.data) throw new Error("Tarefa não encontrada ou sem permissão para atualização.");
      const now = new Date().toISOString();
      return legacyTaskResult(taskContext, { ...current, status: "pending", completedAt: undefined, updatedAt: now }, "reopened", now);
    }
    const result = await client().rpc("reopen_task", {
      p_task_id: safeTaskId,
      p_command_id: taskContext.commandId,
      p_correlation_id: taskContext.correlationId,
      p_causation_id: taskContext.causationId ?? null,
      p_actor_type: taskContext.actorType,
      p_actor_id: taskContext.actorId,
      p_origin: taskContext.origin,
      p_idempotency_key: taskContext.idempotencyKey,
      p_requested_at: taskContext.requestedAt,
      p_expected_version: taskContext.expectedVersion ?? null,
    });
    if (result.error) throw result.error;
    return parseTaskCommandResult(result.data);
  },
  /* Legacy direct update intentionally retained only as historical text; the command RPC above is the active path.
    const result = await client()
      .from("tasks")
      .update({ status: "pending", completed_at: null })
      .eq("id", id)
      .select("id")
      .maybeSingle();
    if (result.error) throw result.error;
    if (!result.data)
      throw new Error(
        "Tarefa não encontrada ou sem permissão para atualização",
      );
  },
  */
  async renameStage(id: string, name: string) {
    const invalidMessage = "A atualização da etapa é inválida.";
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    const result = await client()
      .from("stages")
      .update({ name: requiredInputText(name, "name", invalidMessage) })
      .eq("id", requiredInputIdentifier(id, "id", invalidMessage))
      .eq("owner_id", owner_id)
      .select("id")
      .maybeSingle();
    if (result.error) throw result.error;
    if (!result.data)
      throw new Error("Etapa não encontrada ou sem permissão para atualização");
  },
  async updateFunnel(id: string, patch: { name: string; description: string }) {
    const invalidMessage = "A atualização do funil é inválida.";
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    const result = await client()
      .from("funnels")
      .update({
        name: requiredInputText(patch.name, "name", invalidMessage),
        description: inputTextValue(patch.description, "description", invalidMessage),
      })
      .eq("id", requiredInputIdentifier(id, "id", invalidMessage))
      .eq("owner_id", owner_id)
      .select("id")
      .maybeSingle();
    if (result.error) throw result.error;
    if (!result.data)
      throw new Error("Funil não encontrado ou sem permissão para atualização");
  },
  async updateConfigColor(kind: "stage" | "tag" | "trigger", id: string, color: string) {
    const invalidMessage = "A atualização de cor é inválida.";
    if (kind !== "stage" && kind !== "tag" && kind !== "trigger") throw new Error(`${invalidMessage}: kind.`);
    const table = kind === "stage" ? "stages" : kind === "tag" ? "tags" : "triggers";
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    const result = await client()
      .from(table)
      .update({ color: requiredInputText(color, "color", invalidMessage) })
      .eq("id", requiredInputIdentifier(id, "id", invalidMessage))
      .eq("owner_id", owner_id)
      .select("id")
      .maybeSingle();
    if (result.error) throw result.error;
    if (!result.data)
      throw new Error(
        "Configuração não encontrada ou sem permissão para atualização",
      );
  },
  async renameConfig(kind: "tag" | "trigger" | "lossReason", id: string, name: string) {
    const invalidMessage = "A atualização de configuração é inválida.";
    if (kind !== "tag" && kind !== "trigger" && kind !== "lossReason") throw new Error(`${invalidMessage}: kind.`);
    const table = kind === "tag" ? "tags" : kind === "trigger" ? "triggers" : "loss_reasons";
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    const result = await client()
      .from(table)
      .update({ name: requiredInputText(name, "name", invalidMessage) })
      .eq("id", requiredInputIdentifier(id, "id", invalidMessage))
      .eq("owner_id", owner_id)
      .select("id")
      .maybeSingle();
    if (result.error) throw result.error;
    if (!result.data)
      throw new Error(
        "Configuração não encontrada ou sem permissão para atualização",
      );
  },
  async updateLead(id: unknown, patch: unknown) {
    const parsed = parseRemoteLeadUpdateInput(id, patch);
    const invalidMessage = "A atualização do lead é inválida.";
    const c = client();
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    const lead = await requireOwnedLeadContext(c, owner_id, parsed.leadId, invalidMessage);
    const tagIds = parsed.tagIds === undefined
      ? undefined
      : await requireOwnedCatalogReferences(c, "tags", owner_id, parsed.tagIds, "tag_id", lead.funnelId, invalidMessage);
    const triggerIds = parsed.triggerIds === undefined
      ? undefined
      : await requireOwnedCatalogReferences(c, "triggers", owner_id, parsed.triggerIds, "trigger_id", lead.funnelId, invalidMessage);
    const { row } = parsed;
    if (Object.keys(row).length) {
      const result = await c
        .from("leads")
        .update(row)
        .eq("id", lead.leadId)
        .eq("owner_id", owner_id)
        .select("id")
        .maybeSingle();
      if (result.error) throw result.error;
      if (!result.data)
        throw new Error(
          "Lead não encontrado ou sem permissão para atualização",
        );
    }
    if (tagIds) {
      const removed = await c
          .from("lead_tags")
          .delete()
          .eq("lead_id", lead.leadId)
          .eq("owner_id", owner_id);
      if (removed.error) throw removed.error;
      if (tagIds.length) {
        const inserted = await c
          .from("lead_tags")
          .insert(
            tagIds.map((tagId: string) => ({
              owner_id,
              lead_id: lead.leadId,
              tag_id: tagId,
            })),
          );
        if (inserted.error) throw inserted.error;
      }
    }
    if (triggerIds) {
      const removed = await c
          .from("lead_triggers")
          .delete()
          .eq("lead_id", lead.leadId)
          .eq("owner_id", owner_id);
      if (removed.error) throw removed.error;
      if (triggerIds.length) {
        const inserted = await c
          .from("lead_triggers")
          .insert(
            triggerIds.map((triggerId: string) => ({
              owner_id,
              lead_id: lead.leadId,
              trigger_id: triggerId,
            })),
          );
        if (inserted.error) throw inserted.error;
      }
    }
  },
  async saveLeadFieldValues(leadId: string, values: Record<string, unknown>) {
    const invalidMessage = "Os valores de campos do lead são inválidos.";
    const c = client();
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    const leadReference = await requireOwnedLeadContext(c, owner_id, leadId, invalidMessage);
    const safeLeadId = leadReference.leadId;
    const payload = rpcRecord(values, invalidMessage);
    const entries = inputKeys(payload, invalidMessage).map((fieldId) => ({
      fieldId: requiredInputIdentifier(fieldId, "field_id", invalidMessage),
      value: rpcField(payload, fieldId, invalidMessage),
    }));
    if (new Set(entries.map((entry) => entry.fieldId)).size !== entries.length)
      throw new Error(`${invalidMessage}: field_id.`);
    const fieldTypes = new Map<string, LeadFieldType>();
    if (entries.length) {
      const definitions = await c
        .from("lead_field_definitions")
        .select("id,owner_id,funnel_id,field_type")
        .in("id", entries.map((entry) => entry.fieldId))
        .eq("owner_id", owner_id);
      if (definitions.error) throw definitions.error;
      for (const value of remoteRows(definitions.data, "lead_field_definitions")) {
        const row = rpcRecord(value, invalidMessage);
        const definitionOwnerId = requiredInputIdentifier(requiredRpcString(row, "owner_id", invalidMessage), "owner_id", invalidMessage);
        if (definitionOwnerId !== owner_id) throw new Error(`${invalidMessage}: owner_id.`);
        const fieldId = requiredInputIdentifier(requiredRpcString(row, "id", invalidMessage), "id", invalidMessage);
        const definitionFunnelId = requiredInputIdentifier(requiredRpcString(row, "funnel_id", invalidMessage), "funnel_id", invalidMessage);
        if (leadReference.funnelId !== undefined && definitionFunnelId !== leadReference.funnelId)
          throw new Error(`${invalidMessage}: field_id/funnel_id.`);
        if (fieldTypes.has(fieldId)) throw new Error(`${invalidMessage}: field_id.`);
        fieldTypes.set(fieldId, requiredRpcEnum(row, "field_type", leadFieldTypes, invalidMessage));
      }
      if (fieldTypes.size !== entries.length) throw new Error(`${invalidMessage}: field_id.`);
      for (const entry of entries) {
        if (!fieldTypes.has(entry.fieldId)) throw new Error(`${invalidMessage}: field_id.`);
      }
    }
    const parsedValues = new Map<string, LeadFieldValue["value"]>();
    const expectedOptionFieldsById = new Map<string, Set<string>>();
    for (const entry of entries) {
      const fieldType = fieldTypes.get(entry.fieldId);
      if (!fieldType) throw new Error(`${invalidMessage}: field_id.`);
      const parsedValue = parseRemoteLeadFieldValue(entry.value, fieldType, invalidMessage);
      parsedValues.set(entry.fieldId, parsedValue);
      const optionIds = fieldType === "select"
        ? parsedValue === null ? [] : [parsedValue]
        : fieldType === "multi_select" && Array.isArray(parsedValue) ? parsedValue : [];
      for (const optionId of optionIds) {
        const safeOptionId = requiredInputIdentifier(optionId, "option_id", invalidMessage);
        const expectedFields = expectedOptionFieldsById.get(safeOptionId) ?? new Set<string>();
        expectedFields.add(entry.fieldId);
        expectedOptionFieldsById.set(safeOptionId, expectedFields);
      }
    }
    const optionReferencesById = new Map<string, { fieldId: string }>();
    const expectedOptionIds = [...expectedOptionFieldsById.keys()];
    if (expectedOptionIds.length) {
      const options = await c
        .from("lead_field_options")
        .select("id,owner_id,field_id")
        .in("id", expectedOptionIds)
        .eq("owner_id", owner_id);
      if (options.error) throw options.error;
      for (const value of remoteRows(options.data, "lead_field_options")) {
        const row = rpcRecord(value, invalidMessage);
        const optionId = requiredInputIdentifier(requiredRpcString(row, "id", invalidMessage), "option_id", invalidMessage);
        if (!expectedOptionFieldsById.has(optionId)) continue;
        if (optionReferencesById.has(optionId)) throw new Error(`${invalidMessage}: option_id.`);
        const optionOwnerId = requiredInputIdentifier(requiredRpcString(row, "owner_id", invalidMessage), "owner_id", invalidMessage);
        if (optionOwnerId !== owner_id) throw new Error(`${invalidMessage}: owner_id.`);
        optionReferencesById.set(optionId, {
          fieldId: requiredInputIdentifier(requiredRpcString(row, "field_id", invalidMessage), "field_id", invalidMessage),
        });
      }
      for (const [optionId, expectedFields] of expectedOptionFieldsById) {
        const option = optionReferencesById.get(optionId);
        if (!option || !expectedFields.has(option.fieldId)) throw new Error(`${invalidMessage}: option_id/field_id.`);
      }
    }
    for (const entry of entries) {
      const optionIds = new Set(
        [...expectedOptionFieldsById]
          .filter(([optionId, fields]) => fields.has(entry.fieldId) && optionReferencesById.get(optionId)?.fieldId === entry.fieldId)
          .map(([optionId]) => optionId),
      );
      assertRemoteLeadFieldValueOptions(fieldTypes.get(entry.fieldId)!, parsedValues.get(entry.fieldId)!, optionIds, invalidMessage);
    }
    const rows = entries.map(({ fieldId }) => ({
      owner_id,
      lead_id: safeLeadId,
      field_id: fieldId,
      value_json: parsedValues.get(fieldId)!,
    }));
    const removed = await c.from("lead_field_values").delete().eq("lead_id", safeLeadId).eq("owner_id", owner_id);
    if (removed.error) throw removed.error;
    if (!rows.length) return;
    const inserted = await c.from("lead_field_values").insert(rows);
    if (inserted.error) throw inserted.error;
  },
  async addFormSection(section: LeadFormSection) {
    const parsed = parseFormSectionInput(section);
    const invalidMessage = "A seção de formulário para persistência é inválida.";
    const c = client();
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    await requireOwnedRemoteRow(c, "funnels", owner_id, parsed.funnelId, "funnel_id", "owner_id", invalidMessage);
    const result = await c.from("lead_form_sections").insert({
      id: parsed.id,
      owner_id,
      funnel_id: parsed.funnelId,
      name: parsed.name,
      position: parsed.order,
      archived_at: parsed.archived ? new Date().toISOString() : null,
    });
    if (result.error) throw result.error;
  },
  async updateFormSection(id: string, patch: Partial<LeadFormSection>) {
    const invalidMessage = "A atualização da seção de formulário é inválida.";
    const safeId = requiredInputIdentifier(id, "id", invalidMessage);
    const payload = rpcRecord(patch, invalidMessage);
    assertAllowedInputKeys(payload, ["name", "order", "archived"], invalidMessage);
    const row: Record<string, unknown> = {};
    if (inputHasField(payload, "name", invalidMessage))
      row.name = requiredInputText(rpcField(payload, "name", invalidMessage), "name", invalidMessage);
    if (inputHasField(payload, "order", invalidMessage))
      row.position = requiredRpcSafeInteger(payload, "order", 0, invalidMessage);
    if (inputHasField(payload, "archived", invalidMessage)) {
      const archived = optionalInputBoolean(payload, "archived", invalidMessage);
      if (archived === undefined) throw new Error(`${invalidMessage}: archived.`);
      row.archived_at = archived ? new Date().toISOString() : null;
    }
    if (!Object.keys(row).length) throw new Error(invalidMessage);
    const c = client();
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    await requireOwnedRemoteRow(c, "lead_form_sections", owner_id, safeId, "section_id", "owner_id", invalidMessage);
    const result = await c.from("lead_form_sections").update(row).eq("id", safeId).eq("owner_id", owner_id).select("id").maybeSingle();
    if (result.error || !result.data) throw result.error ?? new Error("Seção não encontrada");
  },
  async addLeadField(field: LeadFieldDefinition) {
    const parsed = parseLeadFieldInput(field);
    const invalidMessage = "A definição de campo para persistência é inválida.";
    const c = client();
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    await requireOwnedRemoteRow(c, "funnels", owner_id, parsed.funnelId, "funnel_id", "owner_id", invalidMessage);
    await requireOwnedFormSection(c, owner_id, parsed.sectionId, parsed.funnelId, invalidMessage);
    const result = await c.from("lead_field_definitions").insert({
      id: parsed.id,
      owner_id,
      funnel_id: parsed.funnelId,
      section_id: parsed.sectionId,
      name: parsed.name,
      field_key: parsed.key,
      field_type: parsed.type,
      position: parsed.order,
      width: parsed.width,
      height: parsed.height,
      required: parsed.required,
      in_form: parsed.inForm,
      in_metrics: parsed.inMetrics,
      placeholder: parsed.placeholder,
      help_text: parsed.helpText,
      archived_at: parsed.archived ? new Date().toISOString() : null,
    });
    if (result.error) throw result.error;
  },
  async updateLeadField(id: string, patch: Partial<LeadFieldDefinition>) {
    const invalidMessage = "A atualização da definição de campo é inválida.";
    const safeId = requiredInputIdentifier(id, "id", invalidMessage);
    const payload = rpcRecord(patch, invalidMessage);
    assertAllowedInputKeys(payload, [
      "sectionId", "name", "key", "type", "order", "width", "height", "required", "inForm",
      "inMetrics", "placeholder", "helpText", "archived",
    ], invalidMessage);
    const row: Record<string, unknown> = {};
    const sectionId = inputHasField(payload, "sectionId", invalidMessage)
      ? requiredInputIdentifier(rpcField(payload, "sectionId", invalidMessage), "sectionId", invalidMessage)
      : undefined;
    if (sectionId !== undefined) row.section_id = sectionId;
    if (inputHasField(payload, "name", invalidMessage))
      row.name = requiredInputText(rpcField(payload, "name", invalidMessage), "name", invalidMessage);
    if (inputHasField(payload, "key", invalidMessage)) {
      const key = requiredInputText(rpcField(payload, "key", invalidMessage), "key", invalidMessage);
      if (!/^[a-z][a-z0-9_]*$/.test(key)) throw new Error(`${invalidMessage}: key.`);
      row.field_key = key;
    }
    if (inputHasField(payload, "type", invalidMessage))
      row.field_type = requiredRpcEnum(payload, "type", leadFieldTypes, invalidMessage);
    if (inputHasField(payload, "order", invalidMessage))
      row.position = requiredRpcSafeInteger(payload, "order", 0, invalidMessage);
    if (inputHasField(payload, "width", invalidMessage))
      row.width = requiredRpcEnum(payload, "width", leadFieldWidths, invalidMessage);
    if (inputHasField(payload, "height", invalidMessage))
      row.height = requiredRpcEnum(payload, "height", leadFieldHeights, invalidMessage);
    if (inputHasField(payload, "required", invalidMessage))
      row.required = requiredRpcBoolean(payload, "required", invalidMessage);
    if (inputHasField(payload, "inForm", invalidMessage))
      row.in_form = requiredRpcBoolean(payload, "inForm", invalidMessage);
    if (inputHasField(payload, "inMetrics", invalidMessage)) {
      const inMetrics = optionalInputBoolean(payload, "inMetrics", invalidMessage);
      if (inMetrics === undefined) throw new Error(`${invalidMessage}: inMetrics.`);
      row.in_metrics = inMetrics;
    }
    if (inputHasField(payload, "placeholder", invalidMessage))
      row.placeholder = optionalInputText(payload, "placeholder", invalidMessage);
    if (inputHasField(payload, "helpText", invalidMessage))
      row.help_text = optionalInputText(payload, "helpText", invalidMessage);
    if (inputHasField(payload, "archived", invalidMessage)) {
      const archived = optionalInputBoolean(payload, "archived", invalidMessage);
      if (archived === undefined) throw new Error(`${invalidMessage}: archived.`);
      row.archived_at = archived ? new Date().toISOString() : null;
    }
    if (!Object.keys(row).length) throw new Error(invalidMessage);
    const c = client();
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    const current = await requireOwnedRemoteRow(c, "lead_field_definitions", owner_id, safeId, "field_id", "owner_id,funnel_id", invalidMessage);
    if (sectionId !== undefined) {
      const funnelId = requiredInputIdentifier(requiredRpcString(current.row, "funnel_id", invalidMessage), "funnel_id", invalidMessage);
      await requireOwnedFormSection(c, owner_id, sectionId, funnelId, invalidMessage);
    }
    const result = await c.from("lead_field_definitions").update(row).eq("id", safeId).eq("owner_id", owner_id).select("id").maybeSingle();
    if (result.error || !result.data) throw result.error ?? new Error("Campo não encontrado");
  },
  async addLeadFieldOption(option: LeadFieldOption) {
    const parsed = parseLeadFieldOptionInput(option);
    const invalidMessage = "A opção de campo para persistência é inválida.";
    const c = client();
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    await requireOwnedRemoteRow(c, "lead_field_definitions", owner_id, parsed.fieldId, "field_id", "owner_id", invalidMessage);
    const result = await c.from("lead_field_options").insert({
      id: parsed.id,
      owner_id,
      field_id: parsed.fieldId,
      name: parsed.name,
      color: parsed.color,
      position: parsed.order,
      archived_at: parsed.archived ? new Date().toISOString() : null,
    });
    if (result.error) throw result.error;
  },
  async createTask(task: TaskDraftInput, context?: CommandContext): Promise<CommandResult<TaskCommandValue>> {
    const draft = taskDraftSchema.parse(task);
    const taskContext = await repositoryTaskContext("tasks.create", context);
    if (!(await taskCommandRpcsEnabled())) {
      const invalidMessage = "A tarefa para persistência é inválida.";
      const c = client();
      const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
      const references = await requireOwnedTaskReferences(
        c,
        owner_id,
        draft.leadId,
        draft.membershipId,
        invalidMessage,
      );
      const now = new Date().toISOString();
      const created: Task = {
        id: crypto.randomUUID(),
        leadId: references.leadId,
        membershipId: references.membershipId,
        description: draft.description,
        dueAt: draft.dueAt,
        priority: draft.priority,
        status: "pending",
        progress: draft.progress,
        note: draft.note || undefined,
        createdAt: now,
        updatedAt: now,
        version: 1,
        origin: draft.origin,
        actionType: draft.actionType,
        sensitive: draft.sensitive,
        immutableReference: draft.immutableReference,
      };
      const inserted = await c.from("tasks").insert({
        id: created.id,
        owner_id,
        lead_id: created.leadId,
        membership_id: created.membershipId ?? null,
        description: created.description,
        due_at: created.dueAt,
        priority: created.priority,
        note: created.note ?? null,
      });
      if (inserted.error) throw inserted.error;
      return legacyTaskResult(taskContext, created, "created", now);
    }
    const result = await client().rpc("create_task", {
      p_task: {
        lead_id: draft.leadId,
        membership_id: draft.membershipId ?? null,
        description: draft.description,
        due_at: draft.dueAt,
        priority: draft.priority,
        note: draft.note ?? null,
        origin: draft.origin,
        action_type: draft.actionType,
        sensitive: draft.sensitive,
        immutable_reference: draft.immutableReference ?? null,
      },
      p_command_id: taskContext.commandId,
      p_correlation_id: taskContext.correlationId,
      p_causation_id: taskContext.causationId ?? null,
      p_actor_type: taskContext.actorType,
      p_actor_id: taskContext.actorId,
      p_origin: taskContext.origin,
      p_idempotency_key: taskContext.idempotencyKey,
      p_requested_at: taskContext.requestedAt,
      p_expected_version: taskContext.expectedVersion ?? null,
    });
    if (result.error) throw result.error;
    return parseTaskCommandResult(result.data);
  },
  async addTask(task: TaskDraftInput, context?: CommandContext) {
    return this.createTask(task, context);
  },
  /* Legacy multi-call insert intentionally retained only as historical text; createTask is the active path.
    const owner_id = await ownerId(),
      result = await client()
        .from("tasks")
        .insert({
          id: task.id,
          owner_id,
          lead_id: task.leadId,
          description: task.description,
          due_at: task.dueAt,
          priority: task.priority,
          note: task.note,
        });
    if (result.error) throw result.error;
  },
  */
  async addEvent(event: RemoteActivityEventInput) {
    const parsed = parseRemoteActivityEventInput(event);
    const invalidMessage = "O evento para persistência é inválido.";
    const c = client();
    const references = await requireOwnedActivityReferences(
      c,
      requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage),
      parsed.leadId,
      parsed.membershipId,
      parsed.taskId,
      parsed.stageId,
      invalidMessage,
    );
    if (countsAsInteraction(parsed.type)) {
      const result = await c.rpc("record_interaction_event", {
        p_event_id: parsed.id,
        p_lead_id: references.leadId,
        p_event_type: parsed.type,
        p_title: parsed.title,
        p_body: parsed.description ?? null,
        p_occurred_at: parsed.occurredAt,
      });
      if (result.error) throw result.error;
      return;
    }
    const result = await c
        .from("activity_events")
        .insert({
          id: parsed.id,
          owner_id: references.ownerId,
          lead_id: references.leadId,
          membership_id: references.membershipId,
          ...(references.taskId === undefined ? {} : { task_id: references.taskId }),
          event_type: parsed.type,
          title: parsed.title,
          body: parsed.description,
          occurred_at: parsed.occurredAt,
          metadata: references.stageId ? { stage_id: references.stageId } : {},
        });
    if (result.error) throw result.error;
  },
  async addConfig(table: string, row: unknown) {
    const parsed = parseConfigWrite(table, row);
    const invalidMessage = "A configuração para persistência é inválida.";
    const c = client();
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    if (parsed.table !== "funnels")
      await requireOwnedRemoteRow(c, "funnels", owner_id, parsed.row.funnel_id, "funnel_id", "owner_id", invalidMessage);
    return c
      .from(parsed.table)
      .insert({ ...parsed.row, owner_id });
  },
  async archive(table: string, id: string, archived: boolean) {
    const invalidMessage = "A atualização de arquivamento é inválida.";
    const safeTable = requiredTable(table, archiveTables, invalidMessage);
    const safeId = requiredInputIdentifier(id, "id", invalidMessage);
    if (typeof archived !== "boolean") throw new Error(`${invalidMessage}: archived.`);
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    const result = await client()
      .from(safeTable as ArchiveTable)
      .update({ archived_at: archived ? new Date().toISOString() : null })
      .eq("id", safeId)
      .eq("owner_id", owner_id)
      .select("id")
      .maybeSingle();
    if (result.error) throw result.error;
    if (!result.data)
      throw new Error(
        "Registro não encontrado ou sem permissão para atualização",
      );
    return result;
  },
  async deleteConfig(
    kind: "funnel" | "stage" | "tag" | "trigger" | "lossReason",
    id: string,
  ) {
    const invalidMessage = "A exclusão de configuração é inválida.";
    if (kind !== "funnel" && kind !== "stage" && kind !== "tag" && kind !== "trigger" && kind !== "lossReason")
      throw new Error(`${invalidMessage}: kind.`);
    const safeId = requiredInputIdentifier(id, "id", invalidMessage);
    const c = client();
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    const table =
      kind === "funnel"
        ? "funnels"
        : kind === "stage"
          ? "stages"
          : kind === "tag"
            ? "tags"
            : kind === "trigger"
              ? "triggers"
              : "loss_reasons";
    if (kind === "funnel") {
      const stages = await c.from("stages").delete().eq("funnel_id", safeId).eq("owner_id", owner_id);
      if (stages.error) throw stages.error;
    }
    const result = await c.from(table).delete().eq("id", safeId).eq("owner_id", owner_id);
    if (result.error) throw result.error;
  },
  async addLink(link: LeadLink) {
    const parsed = parseLeadLinkInput(link);
    const invalidMessage = "O link para persistência é inválido.";
    const c = client();
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    const leadReference = await requireOwnedLead(c, owner_id, parsed.leadId, invalidMessage);
    const result = await c
        .from("links")
        .insert({
          id: parsed.id,
          owner_id: leadReference.ownerId,
          lead_id: leadReference.leadId,
          label: parsed.label,
          url: parsed.url,
        });
    if (result.error) throw result.error;
  },
  async saveView(view: SavedView) {
    const parsed = parseSavedViewInput(view);
    const invalidMessage = "A visualização para persistência é inválida.";
    const c = client();
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    await requireOwnedSavedViewReferences(c, owner_id, parsed, invalidMessage);
    const result = await c
        .from("saved_views")
        .insert({
          id: parsed.id,
          owner_id,
          name: parsed.name,
          filters: {
            schemaVersion: parsed.schemaVersion,
            query: parsed.query,
            funnelId: parsed.funnelId,
            stageId: parsed.stageId,
            status: parsed.status,
            priority: parsed.priority,
            reasonId: parsed.reasonId,
            tagId: parsed.tagId,
            triggerId: parsed.triggerId,
            segment: parsed.segment,
            role: parsed.role,
            source: parsed.source,
            stale: parsed.stale,
            period: parsed.period,
            periodStart: parsed.periodStart,
            periodEnd: parsed.periodEnd,
            columns: parsed.columns,
            sorting: parsed.sorting,
            view: parsed.view,
            heatBand: parsed.heatBand,
            minValue: parsed.minValue,
            maxValue: parsed.maxValue,
          },
        });
    if (result.error) throw result.error;
  },
  async deleteView(id: string) {
    const invalidMessage = "A exclusão da visualização é inválida.";
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    const result = await client().from("saved_views").delete()
      .eq("id", requiredInputIdentifier(id, "id", invalidMessage))
      .eq("owner_id", owner_id);
    if (result.error) throw result.error;
  },
  async uploadPhoto(leadId: string, file: File) {
    const invalidMessage = "O arquivo de foto é inválido.";
    const c = client();
    const owner_id = await ownerId();
    const upload = validateUploadFile(file, photoMimeTypes, invalidMessage);
    const reference = await requireOwnedLead(c, owner_id, leadId, invalidMessage);
    const ext = photoExtensions[upload.type as (typeof photoMimeTypes)[number]];
    const path = `${reference.ownerId}/${reference.leadId}/avatar.${ext}`;
    const { error } = await c
      .storage.from(leadFilesBucket)
      .upload(path, file, { upsert: true, contentType: upload.type });
    if (error) throw error;
    const updated = await c
      .from("leads")
      .update({ photo_path: path })
      .eq("id", reference.leadId)
      .eq("owner_id", reference.ownerId)
      .select("id")
      .maybeSingle();
    if (updated.error || !updated.data) {
      const cleanup = await c.storage.from(leadFilesBucket).remove([path]);
      if (cleanup.error)
        console.warn("A foto foi enviada, mas não foi possível limpar o objeto após a falha do Lead", cleanup.error);
      if (updated.error) throw updated.error;
      throw new Error("Lead não encontrado ou sem permissão para atualização");
    }
    const { data, error: signedError } = await c
      .storage.from(leadFilesBucket)
      .createSignedUrl(path, 3600);
    if (signedError) throw signedError;
    return data?.signedUrl ?? "";
  },
  async uploadAttachment(leadId: string, file: File) {
    const invalidMessage = "O arquivo anexado é inválido.";
    const c = client();
    const owner_id = await ownerId();
    const upload = validateUploadFile(file, attachmentMimeTypes, invalidMessage);
    const reference = await requireOwnedLead(c, owner_id, leadId, invalidMessage);
    const id = requiredStorageSegment(crypto.randomUUID(), "id", invalidMessage);
    const path = `${reference.ownerId}/${reference.leadId}/${id}-${upload.name}`;
    const { error } = await c
      .storage.from(leadFilesBucket)
      .upload(path, file, { contentType: upload.type });
    if (error) throw error;
    const { error: metadataError } = await c
      .from("attachments")
      .insert({
        id,
        owner_id: reference.ownerId,
        lead_id: reference.leadId,
        storage_path: path,
        filename: upload.name,
        mime_type: upload.type,
        size_bytes: upload.size,
      });
    if (metadataError) {
      await c.storage.from(leadFilesBucket).remove([path]);
      throw metadataError;
    }
    const { data } = await c
      .storage.from(leadFilesBucket)
      .createSignedUrl(path, 3600);
    return {
      id,
      leadId: reference.leadId,
      name: upload.name,
      type: upload.type,
      size: upload.size,
      dataUrl: data?.signedUrl,
      createdAt: new Date().toISOString(),
    };
  },
  async deleteAttachment(id: string) {
    const invalidMessage = "Uma referência de anexo é inválida.";
    const c = client();
    const owner_id = requiredInputIdentifier(await ownerId(), "owner_id", invalidMessage);
    const safeId = requiredStorageSegment(id, "id", invalidMessage);
    const { data: attachment, error: lookupError } = await c
      .from("attachments")
      .select("owner_id,lead_id,storage_path,bucket")
      .eq("id", safeId)
      .eq("owner_id", owner_id)
      .maybeSingle();
    if (lookupError) throw lookupError;
    if (!attachment) return;
    const reference = parseRemoteStorageReference(attachment, invalidMessage);
    if (reference.ownerId !== owner_id)
      throw new Error(`${invalidMessage}: owner_id.`);
    assertOwnedStoragePath(reference.storagePath, reference.ownerId, reference.leadId, invalidMessage);
    await requireOwnedLead(c, owner_id, reference.leadId, invalidMessage);
    const result = await c.storage.from(reference.bucket).remove([reference.storagePath]);
    const storageError = result.error ?? undefined;
    const { error: deleteError } = await c
      .from("attachments")
      .delete()
      .eq("id", safeId)
      .eq("owner_id", owner_id);
    if (deleteError) throw deleteError;
    if (storageError)
      console.warn(
        "Metadado excluído, mas não foi possível remover o arquivo do Storage",
        storageError,
      );
  },
  async deleteLeadPermanently(id: string) {
    const invalidMessage = "Os arquivos do lead são inválidos.";
    const c = client();
    const owner_id = await ownerId();
    const safeLeadId = requiredStorageSegment(id, "lead_id", invalidMessage);
    const leadResult = await c
      .from("leads")
      .select("owner_id,photo_path")
      .eq("id", safeLeadId)
      .eq("owner_id", owner_id)
      .maybeSingle();
    if (leadResult.error) throw leadResult.error;
    if (!leadResult.data) return;
    const leadRow = rpcRecord(leadResult.data, invalidMessage);
    const leadOwnerId = requiredStorageSegment(requiredRpcString(leadRow, "owner_id", invalidMessage), "owner_id", invalidMessage);
    if (leadOwnerId !== owner_id) throw new Error(`${invalidMessage}: owner_id.`);
    const photoPath = optionalRemoteStoragePath(leadRow, "photo_path", invalidMessage);
    if (photoPath) assertOwnedStoragePath(photoPath, owner_id, safeLeadId, invalidMessage);
    const [membershipsResult, attachmentsResult] = await Promise.all([
      c
        .from("pipeline_memberships")
        .select("id,owner_id,lead_id")
        .eq("lead_id", safeLeadId)
        .eq("owner_id", owner_id),
      c
        .from("attachments")
        .select("owner_id,lead_id,bucket,storage_path")
        .eq("lead_id", safeLeadId)
        .eq("owner_id", owner_id),
    ]);
    if (membershipsResult.error) throw membershipsResult.error;
    if (attachmentsResult.error) throw attachmentsResult.error;
    const membershipIds = remoteRows(membershipsResult.data, "pipeline_memberships").map((row) => {
      const membership = rpcRecord(row, invalidMessage);
      const membershipOwnerId = requiredStorageSegment(
        requiredRpcString(membership, "owner_id", invalidMessage),
        "owner_id",
        invalidMessage,
      );
      const membershipLeadId = requiredStorageSegment(
        requiredRpcString(membership, "lead_id", invalidMessage),
        "lead_id",
        invalidMessage,
      );
      if (membershipOwnerId !== owner_id || membershipLeadId !== safeLeadId)
        throw new Error(`${invalidMessage}: membership owner/lead.`);
      return requiredStorageSegment(requiredRpcString(membership, "id", invalidMessage), "id", invalidMessage);
    });
    const attachmentReferences = remoteRows(attachmentsResult.data, "attachments").map((row) =>
      parseRemoteStorageReference(row, invalidMessage),
    );
    for (const reference of attachmentReferences) {
      if (reference.ownerId !== owner_id || reference.leadId !== safeLeadId)
        throw new Error(`${invalidMessage}: owner_id/lead_id.`);
      assertOwnedStoragePath(reference.storagePath, owner_id, safeLeadId, invalidMessage);
    }
    const paths = attachmentReferences.map((reference) => ({
      bucket: reference.bucket,
      path: reference.storagePath,
    }));
    if (photoPath) paths.push({ bucket: leadFilesBucket, path: photoPath });
    const removeByLead = [
      "attachments",
      "links",
      "lead_tags",
      "lead_triggers",
      "lead_classification_values",
      "custom_field_values",
      "activity_events",
      "tasks",
    ];
    for (const table of removeByLead) {
      const result = await c
        .from(table)
        .delete()
        .eq("lead_id", safeLeadId)
        .eq("owner_id", owner_id);
      if (result.error) throw result.error;
    }
    if (membershipIds.length) {
      const visits = await c
        .from("stage_visits")
        .delete()
        .in("membership_id", membershipIds)
        .eq("owner_id", owner_id);
      if (visits.error) throw visits.error;
    }
    const memberships = await c
      .from("pipeline_memberships")
      .delete()
      .eq("lead_id", safeLeadId)
      .eq("owner_id", owner_id);
    if (memberships.error) throw memberships.error;
    const leadDelete = await c
      .from("leads")
      .delete()
      .eq("id", safeLeadId)
      .eq("owner_id", owner_id);
    if (leadDelete.error) throw leadDelete.error;
    for (const bucket of [...new Set(paths.map((item) => item.bucket))]) {
      const bucketPaths = paths
        .filter((item) => item.bucket === bucket)
        .map((item) => item.path);
      if (bucketPaths.length) {
        const storageResult = await c.storage.from(bucket).remove(bucketPaths);
        if (storageResult.error)
          console.warn(
            "Lead removido, mas alguns arquivos não puderam ser removidos do Storage",
            storageResult.error,
          );
      }
    }
  },
};
